import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import LeagueSettings from '../components/LeagueSettings';
import DynamicPickScoringNote from '../components/DynamicPickScoringNote';
// Inline utilities aligned with backend/services/matchLockService.js (lock buffer + date parsing)
const FALLBACK_LOCK_BUFFER_MINUTES = 2;

function extractOrdinalMatchLabel(full: string | null | undefined): string | null {
  if (!full) return null;
  const m = full.match(/(\d+)(st|nd|rd|th)\s+Match/i);
  if (m) return `${m[1]}${m[2].toLowerCase()} Match`;
  return null;
}

function formatBreakdownDateShort(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return String(iso).slice(0, 10);
  }
}

function getCurrentGMTTime(): Date {
  return new Date();
}

function ianaToOffsetSuffix(iana: string | null | undefined): string | null {
  if (!iana || typeof iana !== 'string') return null;
  const k = iana.trim();
  if (k === 'Asia/Kolkata' || k === 'IST') return '+05:30';
  return null;
}

/** Parse match instant; pass match when available so naive DB timestamps use live_tournaments.naive_match_date_timezone. */
function parseMatchDateToGMT(
  dateString: string | null | undefined,
  match?: { naive_match_date_timezone?: string | null } | null
): Date | null {
  if (!dateString) return null;
  try {
    const offsetSuffix = ianaToOffsetSuffix(match?.naive_match_date_timezone ?? null);
    const ds = String(dateString);
    if (ds.includes('T') && !ds.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ds)) {
      return new Date(`${ds}Z`);
    }
    if (ds.includes('T') && ds.endsWith('Z')) {
      return new Date(ds);
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(ds)) {
      return new Date(`${ds}T00:00:00Z`);
    }
    const pgTimestamp = ds.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?)$/);
    if (pgTimestamp) {
      const timePart = pgTimestamp[2].replace(/\.\d+$/, '');
      const isoLocal = `${pgTimestamp[1]}T${timePart}`;
      if (offsetSuffix) {
        return new Date(isoLocal + offsetSuffix);
      }
      return new Date(`${isoLocal}Z`);
    }
    const d = new Date(ds);
    return isNaN(d.getTime()) ? null : d;
  } catch (error) {
    console.error('Error parsing match date:', error);
    return null;
  }
}

/** Mirrors backend isMatchLocked: status first, then time − buffer. */
function isPickemMatchLocked(match: Match, lockBufferMinutes: number): boolean {
  if (!match) return false;
  if (match.status === 'live' || match.status === 'completed' || match.status === 'abandoned' || (match as any).matchStarted) {
    return true;
  }
  if (!match.match_date) return false;
  const matchDateGMT = parseMatchDateToGMT(match.match_date, match);
  if (!matchDateGMT) return false;
  const nowGMT = getCurrentGMTTime();
  const lockTime = new Date(matchDateGMT.getTime() - lockBufferMinutes * 60 * 1000);
  return nowGMT >= lockTime;
}

function getMatchStatus(match: any): string {
  if (match.status === 'live') {
    return 'Live';
  }
  
  if (match.status === 'completed') {
    return 'Completed';
  }
  
  if (match.match_date) {
    const matchDateGMT = parseMatchDateToGMT(match.match_date, match);
    if (matchDateGMT) {
      const nowGMT = getCurrentGMTTime();
      if (nowGMT >= matchDateGMT) {
        return 'Started';
      }
    }
  }
  
  return 'Upcoming';
}

/** Display label for win margin value (easy/medium/hard) in result summaries */
function formatWinMarginLabel(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  const v = String(value).toLowerCase();
  if (v === 'easy') return 'Easy';
  if (v === 'hard') return 'Hard';
  if (v === 'medium') return 'Medium';
  return value;
}

/** Venue line on match cards: real stadium/city when set, else TBD until populated in admin/schedule. */
function pickemVenueDisplayLabel(venue: string | null | undefined): string {
  const v = venue != null ? String(venue).trim() : '';
  if (!v || v.toUpperCase() === 'TBD') return 'TBD';
  return v;
}

interface PickemLeague {
  id: number;
  name: string;
  tournament_name: string;
  format: string;
  year: number;
  max_participants: number;
  current_participants: number;
  is_public: boolean;
  join_code?: string;
  passcode?: string;
  created_by: number;
  status: string;
  created_at?: string;
  dynamic_scoring_enabled?: boolean;
  /** League creator opt-in: email all members a picks table once per match (first 5 min after start). */
  pickem_email_match_start_pick_summary?: boolean;
  /** When true, All Picks shows all fixtures before lock; API returns full all-predictions. */
  pickem_all_picks_always_visible?: boolean;
}

interface Participant {
  id: number;
  user_id: number;
  display_name: string;
  role: string;
  joined_at: string;
  is_manager: boolean;
  /** Per-league opt-in: email after each save (batched by transaction). */
  pickem_email_prediction_confirmation?: boolean;
}

interface Match {
  id: string;
  name: string;
  team1_name: string;
  team2_name: string;
  team1_shortname?: string;
  team2_shortname?: string;
  match_date: string;
  venue: string;
  status: string;
  winner?: string;
  match_number?: number;
  /** From live_tournaments when match comes from tournament_match_results */
  naive_match_date_timezone?: string | null;
  squad_players?: Array<{
    id: number;
    name: string;
    team: string;
    role: string;
  }>;
}

interface Prediction {
  id: number;
  match_id: string;
  predicted_winner: string | null;
  is_correct?: boolean;
  points_awarded: number;
  created_at: string;
  match_name: string;
  team1_name: string;
  team2_name: string;
  match_date: string;
  status: string;
  winner?: string;
  // Enhanced prediction fields
  total_runs_prediction?: number;
  mom_player_id?: number;
  mom_player_name?: string;
  win_margin_prediction?: string;
  points_breakdown?: any;
  total_points?: number;
  // Match result fields (from pickem_match_results)
  total_runs_result?: number;
  mom_result?: string;
  win_margin_result?: string;
  // Match result fields (from tournament_match_results - fallback)
  tournament_total_runs?: number;
  tournament_man_of_match?: string;
  // Additional fields for All Picks tab
  user_id?: number;
  display_name?: string;
  mom_prediction?: string;
}

interface Standing {
  user_id: number;
  display_name: string;
  total_predictions: number;
  correct_predictions: number;
  wrong_predictions: number;
  total_points: number;
  joined_at: string;
  is_manager: boolean;
  wins?: number;
  losses?: number;
}

/** Per-match drill-down for one fantasy league only (server scopes picks by league). */
interface StandingsBreakdownMatch {
  match_id: string;
  match_name: string | null;
  match_date: string | null;
  status: string | null;
  actual_winner: string | null;
  predicted_winner: string | null;
  points: number;
  points_detail: {
    base: number;
    streak_bonus: number;
    wrong_penalty: number;
    total_runs: number;
    mom: number;
  } | null;
}

interface StandingsBreakdown {
  league_id: number;
  user_id: number;
  display_name: string;
  dynamic_scoring_enabled: boolean;
  total_points: number;
  matches: StandingsBreakdownMatch[];
  phase_id?: string | null;
  phase_label?: string | null;
}

type PhaseMatrixRow = {
  user_id: number;
  display_name: string;
  is_manager: boolean;
  joined_at: string;
  cumulative_points: number;
  phase_points: Record<string, number>;
};

type PickemPredictionActivityEvent = {
  id: number;
  fantasy_league_id: number;
  user_id: number;
  target_user_id: number;
  match_id: string;
  event_type: 'created' | 'updated' | 'cleared';
  old_predicted_winner: string | null;
  new_predicted_winner: string | null;
  source: string;
  transaction_id: string | null;
  created_at: string;
  match_name: string | null;
  match_date: string | null;
};

const PickemLeaguePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Main nav tabs only (activity lives under League Info). Legacy ?tab=activity opens Info + activity section.
  const validTabs = ['info', 'picks', 'all-picks', 'standings'] as const;
  type MainTab = (typeof validTabs)[number];
  const tabFromUrl = searchParams.get('tab');
  const initialMainTab = (): MainTab => {
    if (tabFromUrl === 'activity') return 'info';
    if (tabFromUrl && (validTabs as readonly string[]).includes(tabFromUrl)) return tabFromUrl as MainTab;
    return 'picks';
  };
  const [activeTab, setActiveTab] = useState<MainTab>(initialMainTab);
  const [showLeagueActivity, setShowLeagueActivity] = useState(() => tabFromUrl === 'activity');
  const [userActivity, setUserActivity] = useState<PickemPredictionActivityEvent[]>([]);
  const [userActivityLoading, setUserActivityLoading] = useState(false);
  const [userActivityError, setUserActivityError] = useState<string | null>(null);
  const [pickConfirmationSaving, setPickConfirmationSaving] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [league, setLeague] = useState<PickemLeague | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [allPredictions, setAllPredictions] = useState<any[]>([]);
  const [allPicksLoading, setAllPicksLoading] = useState(false);
  const [standings, setStandings] = useState<Standing[]>([]);
  const [standingsScope, setStandingsScope] = useState<'cumulative' | 'phase'>('cumulative');
  const [standingsPhaseId, setStandingsPhaseId] = useState<string | null>(null);
  const [pickemPhasesConfig, setPickemPhasesConfig] = useState<{
    phases: Array<{ id: string; label: string; match_ids: string[] }>;
  } | null>(null);
  const [standingsCurrentPhaseId, setStandingsCurrentPhaseId] = useState<string | null>(null);
  const [standingsSubview, setStandingsSubview] = useState<'leaderboard' | 'matrix'>('leaderboard');
  const [phaseMatrix, setPhaseMatrix] = useState<{
    phases: Array<{ id: string; label: string }>;
    rows: PhaseMatrixRow[];
  } | null>(null);
  const [phaseMatrixLoading, setPhaseMatrixLoading] = useState(false);
  const [phaseMatrixError, setPhaseMatrixError] = useState<string | null>(null);
  const [matrixSort, setMatrixSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({
    key: 'cumulative',
    dir: 'desc'
  });
  const [standingBreakdown, setStandingBreakdown] = useState<StandingsBreakdown | null>(null);
  const [standingBreakdownLoading, setStandingBreakdownLoading] = useState(false);
  const [standingBreakdownError, setStandingBreakdownError] = useState<string | null>(null);
  const [standingBreakdownExpectedTotal, setStandingBreakdownExpectedTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isManager, setIsManager] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const [selectedAllPicksMatch, setSelectedAllPicksMatch] = useState<string | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  
  // New state for enhanced picks functionality
  const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);
  const [batchPredictions, setBatchPredictions] = useState<{[key: string]: string}>({});
  const [savingPredictions, setSavingPredictions] = useState(false);
  const [viewMode, setViewMode] = useState<'dropdown' | 'list'>('dropdown');
  
  // Enhanced features state
  const [totalRunsPredictions, setTotalRunsPredictions] = useState<{[key: string]: number}>({});
  const [momPredictions, setMomPredictions] = useState<{[key: string]: {id: string | number, name: string}}>({});
  const [winMarginPredictions, setWinMarginPredictions] = useState<{[key: string]: string}>({});
  const [matchSquadPlayers, setMatchSquadPlayers] = useState<{[key: string]: any[]}>({});
  const [leagueSettings, setLeagueSettings] = useState<any>(null);
  const [leagueHasStarted, setLeagueHasStarted] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedLeagueName, setEditedLeagueName] = useState('');
  const [isEditingMaxParticipants, setIsEditingMaxParticipants] = useState(false);
  const [editedMaxParticipants, setEditedMaxParticipants] = useState<number>(0);
  const [isUpdatingMaxParticipants, setIsUpdatingMaxParticipants] = useState(false);
  /** Must match server pickem lock (league_settings.lock_buffer_minutes); default 2 */
  const [pickemLockBufferMinutes, setPickemLockBufferMinutes] = useState(FALLBACK_LOCK_BUFFER_MINUTES);

  useEffect(() => {
    if (id) {
      const leagueId = parseInt(id);
      if (isNaN(leagueId)) {
        setError('Invalid league ID');
        setLoading(false);
        return;
      }
      loadLeagueData();
      loadLeagueSettings();
    } else {
      setError('League ID is required');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setUserActivity([]);
    setUserActivityError(null);
  }, [id]);

  const loadUserActivity = useCallback(async () => {
    if (!id) return;
    try {
      setUserActivityLoading(true);
      setUserActivityError(null);
      const leagueId = parseInt(id, 10);
      const data = await apiService.getPickemUserActivity(leagueId, 200);
      setUserActivity(Array.isArray(data.events) ? data.events : []);
    } catch (err: any) {
      setUserActivityError(err.message || 'Failed to load your activity');
    } finally {
      setUserActivityLoading(false);
    }
  }, [id]);

  const requestPickConfirmationEmail = useCallback(
    async (transactionId: string | undefined) => {
      if (!transactionId || !id) return;
      const uid = currentUserId;
      if (uid == null) return;
      const me = participants.find((p) => p.user_id === uid);
      if (!me?.pickem_email_prediction_confirmation) return;
      try {
        const tz =
          typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined;
        await apiService.sendPickemPickConfirmationEmail(parseInt(id, 10), {
          transactionId,
          ianaTimezone: tz
        });
      } catch (e) {
        console.warn('Pick confirmation email:', e);
      }
    },
    [id, currentUserId, participants]
  );

  // Handle URL tab persistence; legacy ?tab=activity → Info tab + expand activity panel
  useEffect(() => {
    const raw = searchParams.get('tab');
    if (raw === 'activity') {
      setActiveTab('info');
      setShowLeagueActivity(true);
      setSearchParams({ tab: 'info' }, { replace: true });
      return;
    }
    if (raw && (validTabs as readonly string[]).includes(raw) && raw !== activeTab) {
      setActiveTab(raw as MainTab);
    } else if (!raw && activeTab !== 'picks') {
      setSearchParams({ tab: activeTab });
    }
  }, [searchParams, activeTab, setSearchParams]);

  useEffect(() => {
    if (activeTab !== 'info' || !showLeagueActivity) return;
    loadUserActivity();
  }, [activeTab, showLeagueActivity, loadUserActivity]);

  // Squad players are loaded lazily when user opens/uses related controls.

  // Debug momPredictions state changes
  useEffect(() => {
    console.log('🔍 momPredictions state changed:', momPredictions);
  }, [momPredictions]);

  // Debug batch predictions state changes
  useEffect(() => {
    console.log('🔍 batchPredictions state changed:', batchPredictions);
  }, [batchPredictions]);

  // Debug total runs predictions state changes
  useEffect(() => {
    console.log('🔍 totalRunsPredictions state changed:', totalRunsPredictions);
  }, [totalRunsPredictions]);

  // Load existing predictions when predictions data is available
  useEffect(() => {
    if (predictions.length > 0) {
      console.log('🔍 Loading existing predictions into batch state');
      loadExistingPredictions(predictions);
    }
  }, [predictions]);

  // Debug dropdown values
  useEffect(() => {
    console.log('🔍 Current momPredictions for dropdowns:', momPredictions);
    Object.entries(momPredictions).forEach(([matchId, prediction]) => {
      console.log(`🔍 Match ${matchId}: ID=${prediction.id}, Name=${prediction.name}, Value=${prediction.id?.toString()}`);
    });
  }, [momPredictions]);

  // Auto-update selected match in All Picks tab when active match changes.
  // Include pickem_all_picks_always_visible so refetch runs after manager toggles visibility
  // (loadLeagueData clears allPredictions; this effect must run to repopulate).
  useEffect(() => {
    if (activeTab === 'all-picks') {
      const activeMatch = getActiveMatch();
      if (activeMatch) {
        setSelectedAllPicksMatch(activeMatch.id);
      }
      loadAllPicksData();
    }
  }, [activeTab, matches, league?.pickem_all_picks_always_visible]);

  // Check if league has started whenever league or matches data changes
  useEffect(() => {
    const hasStarted = checkIfLeagueHasStarted();
    setLeagueHasStarted(hasStarted);
    console.log('🔍 League has started:', hasStarted);
  }, [league, matches]);



  // Auto-select next match for My Picks dropdown
  useEffect(() => {
    if (activeTab === 'picks' && viewMode === 'dropdown' && matches.length > 0) {
      const nextMatch = getNextMatch();
      if (nextMatch) {
        // Only auto-select if no match is selected at all
        // Don't auto-select if user has manually selected a match (even if it's completed)
        if (!selectedMatchId) {
          console.log('🔍 Auto-selecting next match:', nextMatch.name);
          setSelectedMatchId(nextMatch.id);
        }
      } else if (getUpcomingMatches().length === 0) {
        // Clear selection if no upcoming matches
        setSelectedMatchId(null);
      }
    }
  }, [activeTab, viewMode, matches, selectedMatchId]);

  const loadStandingsTabData = useCallback(async (leagueId: number) => {
    const base = await apiService.getPickemStandings(leagueId);
    const groups = (base as any).pickem_phase_groups;
    const phasesList = groups?.phases;
    if (phasesList?.length) {
      setPickemPhasesConfig(groups);
      setStandingsCurrentPhaseId((base as any).current_phase_id ?? null);
      const cur = (base as any).current_phase_id;
      if (cur) {
        setStandingsScope('phase');
        setStandingsPhaseId(cur);
        const ph = await apiService.getPickemStandings(leagueId, { scope: 'phase', phase_id: cur });
        setStandings((ph as any).standings || []);
      } else {
        setStandingsScope('cumulative');
        setStandingsPhaseId(null);
        setStandings((base as any).standings || []);
      }
    } else {
      setPickemPhasesConfig(null);
      setStandingsCurrentPhaseId(null);
      setStandingsScope('cumulative');
      setStandingsPhaseId(null);
      setStandings((base as any).standings || []);
    }
  }, []);

  const loadPhaseMatrix = useCallback(async () => {
    if (!id) return;
    const leagueId = parseInt(id, 10);
    if (Number.isNaN(leagueId)) return;
    setPhaseMatrixLoading(true);
    setPhaseMatrixError(null);
    try {
      const res = await apiService.getPickemPhaseScoreMatrix(leagueId);
      if ((res as any).success) {
        setPhaseMatrix({
          phases: (res as any).phases || [],
          rows: (res as any).rows || []
        });
      } else {
        setPhaseMatrixError((res as any).message || 'Failed to load matrix');
        setPhaseMatrix(null);
      }
    } catch (e: any) {
      setPhaseMatrixError(e?.message || 'Failed to load matrix');
      setPhaseMatrix(null);
    } finally {
      setPhaseMatrixLoading(false);
    }
  }, [id]);

  const refreshPickemStandings = useCallback(async () => {
    if (!id) return;
    const leagueId = parseInt(id, 10);
    if (Number.isNaN(leagueId)) return;
    if (standingsScope === 'phase' && standingsPhaseId) {
      const d = await apiService.getPickemStandings(leagueId, { scope: 'phase', phase_id: standingsPhaseId });
      setStandings((d as any).standings || []);
    } else {
      const d = await apiService.getPickemStandings(leagueId);
      setStandings((d as any).standings || []);
    }
    if (standingsSubview === 'matrix') {
      await loadPhaseMatrix();
    }
  }, [id, standingsScope, standingsPhaseId, standingsSubview, loadPhaseMatrix]);

  const matchById = useMemo(() => {
    const m = new Map<string, Match>();
    for (const x of matches) m.set(String(x.id), x);
    return m;
  }, [matches]);

  const phaseScheduleSections = useMemo(() => {
    if (!pickemPhasesConfig?.phases?.length) return [];
    return pickemPhasesConfig.phases.map((ph) => {
      const resolved = (ph.match_ids || [])
        .map((mid) => matchById.get(String(mid)))
        .filter((x): x is Match => Boolean(x));
      return { ...ph, resolved };
    });
  }, [pickemPhasesConfig, matchById]);

  const unassignedMatchCount = useMemo(() => {
    if (!pickemPhasesConfig?.phases?.length) return 0;
    const assigned = new Set(
      pickemPhasesConfig.phases.flatMap((p) => (p.match_ids || []).map(String))
    );
    return matches.filter((m) => !assigned.has(String(m.id))).length;
  }, [pickemPhasesConfig, matches]);

  const sortedMatrixRows = useMemo(() => {
    if (!phaseMatrix?.rows?.length) return [];
    const { key, dir } = matrixSort;
    return [...phaseMatrix.rows].sort((a, b) => {
      const na = key === 'cumulative' ? a.cumulative_points : a.phase_points[key] ?? 0;
      const nb = key === 'cumulative' ? b.cumulative_points : b.phase_points[key] ?? 0;
      if (dir === 'desc') {
        if (nb !== na) return nb - na;
      } else if (na !== nb) {
        return na - nb;
      }
      return new Date(a.joined_at).getTime() - new Date(b.joined_at).getTime();
    });
  }, [phaseMatrix, matrixSort]);

  const toggleMatrixSort = (key: string) => {
    setMatrixSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }
    );
  };

  // Function to handle tab changes and update URL
  const handleTabChange = async (newTab: MainTab) => {
    console.log('🔍 Tab change called with:', newTab);
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
    if (newTab !== 'standings') {
      setStandingsSubview('leaderboard');
    }
    if (newTab !== 'info') {
      setShowLeagueActivity(false);
    }
    
    // Reload standings when standings tab is clicked
    if (newTab === 'standings') {
      try {
        const leagueId = parseInt(id!);
        await loadStandingsTabData(leagueId);
      } catch (err) {
        console.error('Failed to load standings:', err);
      }
    }
  };

  const loadLeagueData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!id) {
        setError('League ID is required');
        setLoading(false);
        return;
      }

      const leagueId = parseInt(id);
      if (isNaN(leagueId)) {
        setError('Invalid league ID');
        setLoading(false);
        return;
      }

      const [leagueData, participantsData, matchesData, predictionsData, userProfile] = await Promise.all([
        apiService.getPickemLeague(leagueId),
        apiService.getPickemParticipants(leagueId),
        apiService.getPickemMatches(leagueId),
        apiService.getUserPickemPredictions(leagueId),
        apiService.getUserProfile()
      ]);

      // Lock settings are helpful, but don't block page render if this call fails.
      apiService
        .getMatchLockLeagueSettings(leagueId)
        .then((lockRes) => {
          const raw = lockRes?.lockSettings?.lockBufferMinutes;
          const mins = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
          setPickemLockBufferMinutes(
            Number.isFinite(mins) && mins >= 0 ? mins : FALLBACK_LOCK_BUFFER_MINUTES
          );
        })
        .catch(() => {
          setPickemLockBufferMinutes(FALLBACK_LOCK_BUFFER_MINUTES);
        });

      setLeague(leagueData.league);
      setEditedLeagueName(leagueData.league.name);
      setEditedMaxParticipants(leagueData.league.max_participants);
      setParticipants(participantsData.participants);
      console.log('🔍 Loaded matches data:', matchesData.matches);
      console.log(
        '🔍 Match statuses:',
        matchesData.matches.map((m: Match) => ({
          name: m.name,
          status: m.status,
          date: m.match_date,
          winner: m.winner
        }))
      );
      setPredictions(predictionsData.predictions);

      // Update matches with winner information from predictions
      const updatedMatches = matchesData.matches.map((match: Match) => {
        // Find a prediction for this match that has a winner
        const predictionWithWinner = predictionsData.predictions.find((p: any) => 
          p.match_id === match.id && p.winner
        );
        
        if (predictionWithWinner) {
          console.log('🔍 Updating match with winner from prediction:', match.name, predictionWithWinner.winner);
          return {
            ...match,
            winner: predictionWithWinner.winner
          };
        }
        
        return match;
      });
      
      setMatches(updatedMatches);

      // Pre-load squad data for all matches that have squad_players
      const squadData: {[key: string]: any[]} = {};
      updatedMatches.forEach((match: Match) => {
        if (match.squad_players && match.squad_players.length > 0) {
          squadData[match.id] = match.squad_players;
        }
      });
      setMatchSquadPlayers(squadData);

      // Standings are heavy; fetch eagerly only when opening standings tab.
      if (activeTab === 'standings') {
        await loadStandingsTabData(leagueId);
      } else {
        setStandings([]);
      }

      setCurrentUserId(userProfile.id);
      
      const currentParticipant = participantsData.participants.find(
        (p: Participant) => p.user_id === userProfile.id
      );
      setIsManager(currentParticipant?.role === 'manager');

      // All-picks data is loaded lazily when that tab opens.
      setAllPredictions([]);

    } catch (err: any) {
      setError(err.message || 'Failed to load league data');
    } finally {
      setLoading(false);
    }
  };

  const loadAllPicksData = async () => {
    if (!id) return;
    if (allPicksLoading) return;
    if (allPredictions.length > 0) return;
    const leagueId = parseInt(id, 10);
    if (Number.isNaN(leagueId)) return;
    try {
      setAllPicksLoading(true);
      const allPredictionsData = await apiService.getAllPickemPredictions(leagueId);
      setAllPredictions(Array.isArray(allPredictionsData.predictions) ? allPredictionsData.predictions : []);
    } catch (err) {
      console.error('Failed to load all picks predictions:', err);
      setAllPredictions([]);
    } finally {
      setAllPicksLoading(false);
    }
  };

  const openStandingBreakdown = async (userId: number, expectedTotalPoints: number) => {
    if (!id) return;
    const leagueId = parseInt(id, 10);
    if (Number.isNaN(leagueId)) return;
    setStandingBreakdown(null);
    setStandingBreakdownError(null);
    setStandingBreakdownExpectedTotal(expectedTotalPoints);
    setStandingBreakdownLoading(true);
    try {
      const breakdownOpts =
        standingsScope === 'phase' && standingsPhaseId ? { phase_id: standingsPhaseId } : undefined;
      const res = await apiService.getPickemStandingsUserBreakdown(leagueId, userId, breakdownOpts);
      const breakdown = (res as any)?.breakdown as StandingsBreakdown | undefined;
      if ((res as any)?.success && breakdown) {
        setStandingBreakdown(breakdown);
      } else {
        setStandingBreakdownError((res as any)?.message || 'Could not load breakdown');
      }
    } catch (err: any) {
      setStandingBreakdownError(err?.message || 'Could not load breakdown');
    } finally {
      setStandingBreakdownLoading(false);
    }
  };

  const closeStandingBreakdown = () => {
    setStandingBreakdown(null);
    setStandingBreakdownError(null);
    setStandingBreakdownLoading(false);
    setStandingBreakdownExpectedTotal(null);
  };

  // Load existing predictions into state for persistence
  const loadExistingPredictions = (predictions: any[]) => {
    console.log('🔍 loadExistingPredictions called with:', predictions);
    
    // Load existing team predictions into batch state
    const existingBatchPredictions: {[key: string]: string} = {};
    const existingTotalRuns: {[key: string]: number} = {};
    const existingMomPredictions: {[key: string]: {id: string | number, name: string}} = {};
    const existingWinMarginPredictions: {[key: string]: string} = {};
    
    predictions.forEach(prediction => {
      console.log('🔍 Processing prediction:', prediction);
      
      if (prediction.predicted_winner) {
        existingBatchPredictions[prediction.match_id] = prediction.predicted_winner;
        console.log('🔍 Added team prediction:', prediction.match_id, prediction.predicted_winner);
      }
      
      if (prediction.total_runs_prediction) {
        existingTotalRuns[prediction.match_id] = prediction.total_runs_prediction;
        console.log('🔍 Added total runs prediction:', prediction.match_id, prediction.total_runs_prediction);
      }
      
      if (prediction.mom_player_id && prediction.mom_player_name) {
        existingMomPredictions[prediction.match_id] = {
          id: prediction.mom_player_id,
          name: prediction.mom_player_name
        };
        console.log('🔍 Added MoM prediction:', prediction.match_id, prediction.mom_player_id, prediction.mom_player_name);
      }
      
      if (prediction.win_margin_prediction) {
        existingWinMarginPredictions[prediction.match_id] = prediction.win_margin_prediction;
        console.log('🔍 Added Win Margin prediction:', prediction.match_id, prediction.win_margin_prediction);
      }
    });
    
    console.log('🔍 Setting batch states:', {
      existingBatchPredictions,
      existingTotalRuns,
      existingMomPredictions,
      existingWinMarginPredictions
    });
    
    setBatchPredictions(existingBatchPredictions);
    setTotalRunsPredictions(existingTotalRuns);
    setMomPredictions(existingMomPredictions);
    setWinMarginPredictions(existingWinMarginPredictions);
  };

  const handleMakePrediction = async (matchId: string, predictedWinner: string) => {
    try {
      const leagueId = parseInt(id!);
      
      // Get enhanced prediction data
      const totalRuns = totalRunsPredictions[matchId] || undefined;
      const momPrediction = momPredictions[matchId];
      const momPlayerId = momPrediction?.id || undefined;
      const momPlayerName = momPrediction?.name || undefined;
      const winMarginPrediction = winMarginPredictions[matchId] || undefined;
      
      const saveRes: any = await apiService.makePickemPrediction(
        leagueId,
        matchId,
        predictedWinner,
        totalRuns,
        momPlayerId,
        momPlayerName,
        winMarginPrediction
      );

      void requestPickConfirmationEmail(saveRes?.transactionId);

      // Reload predictions
      const predictionsData = await apiService.getUserPickemPredictions(leagueId);
      setPredictions(predictionsData.predictions);
      
      await refreshPickemStandings();
      
      // Clear the batch prediction for this match since it's now saved
      setBatchPredictions(prev => {
        const newBatch = { ...prev };
        delete newBatch[matchId];
        return newBatch;
      });
      
      // Clear enhanced predictions for this match
      setTotalRunsPredictions(prev => {
        const newPredictions = { ...prev };
        delete newPredictions[matchId];
        return newPredictions;
      });
      
      setMomPredictions(prev => {
        const newPredictions = { ...prev };
        delete newPredictions[matchId];
        return newPredictions;
      });
      
      setWinMarginPredictions(prev => {
        const newPredictions = { ...prev };
        delete newPredictions[matchId];
        return newPredictions;
      });
      
    } catch (err: any) {
      setError(err.message || 'Failed to make prediction');
    }
  };

  const isMatchLockedLocal = (match: Match) =>
    isPickemMatchLocked(match, pickemLockBufferMinutes);

  const getMatchStatusLocal = (match: Match) => {
    // For pick'em leagues, we need to distinguish between:
    // - "Started": Match has passed start time but no result is set
    // - "Completed": Admin has set the match result (winner is determined)
    // - "Abandoned": Match was abandoned
    
    // Check match.status first (this is the source of truth from database)
    if (match.status === 'abandoned') {
      return 'Abandoned';
    }
    
    if (match.status === 'completed') {
      return 'Completed';
    }
    
    if (match.status === 'live') {
      return 'Live';
    }
    
    // Fallback: Check if match has a winner (completed match with result)
    // This is for backward compatibility with old data
    // Exclude "N/A" which is used for abandoned matches
    if (match.winner && match.winner.trim() !== '' && match.winner.trim() !== 'N/A') {
      return 'Completed';
    }
    
    // Check if match has started (passed start time) - using GMT/UTC
    if (match.match_date) {
      const matchDateGMT = parseMatchDateToGMT(match.match_date, match);
      if (matchDateGMT) {
        const nowGMT = getCurrentGMTTime();
        
        if (nowGMT >= matchDateGMT) {
          return 'Started';
        }
      }
    }
    
    return 'Upcoming';
  };

  /** Fixtures where users can still submit or change a pick (not completed/abandoned, not locked). */
  const matchStillAcceptsPicks = (match: Match): boolean => {
    const st = getMatchStatusLocal(match);
    if (st === 'Completed' || st === 'Abandoned') return false;
    if (isMatchLockedLocal(match)) return false;
    return true;
  };

  const getPredictionForMatch = (matchId: string) => {
    return predictions.find(p => String(p.match_id) === String(matchId));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // New helper functions for enhanced picks functionality
  const getUpcomingMatches = () => {
    return matches.filter(matchStillAcceptsPicks).sort((a, b) => {
      const dateA = parseMatchDateToGMT(a.match_date, a);
      const dateB = parseMatchDateToGMT(b.match_date, b);
      return dateA && dateB ? dateA.getTime() - dateB.getTime() : 0;
    });
  };

  const getAllMatchesForDropdown = () => {
    return matches.sort((a, b) => {
      const dateA = parseMatchDateToGMT(a.match_date, a);
      const dateB = parseMatchDateToGMT(b.match_date, b);
      return dateA && dateB ? dateA.getTime() - dateB.getTime() : 0;
    });
  };

  const getMatchesByDate = () => {
    const upcomingMatches = getUpcomingMatches();
    const grouped: {[key: string]: Match[]} = {};
    
    upcomingMatches.forEach(match => {
      const matchDateGMT = parseMatchDateToGMT(match.match_date, match);
      const fallbackDate = new Date(match.match_date);
      const dateKey = matchDateGMT
        ? matchDateGMT.toDateString()
        : Number.isNaN(fallbackDate.getTime())
          ? 'Unknown date'
          : fallbackDate.toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(match);
    });
    
    return grouped;
  };

  /** Past / locked / completed matches for batch view (bottom section). */
  const getNonPickableMatchesByDate = () => {
    const list = matches
      .filter((m) => !matchStillAcceptsPicks(m))
      .sort((a, b) => {
        const dateA = parseMatchDateToGMT(a.match_date, a);
        const dateB = parseMatchDateToGMT(b.match_date, b);
        return dateA && dateB ? dateA.getTime() - dateB.getTime() : 0;
      });
    const grouped: { [key: string]: Match[] } = {};
    list.forEach((match) => {
      const matchDateGMT = parseMatchDateToGMT(match.match_date, match);
      const fallbackDate = new Date(match.match_date);
      const dateKey = matchDateGMT
        ? matchDateGMT.toDateString()
        : Number.isNaN(fallbackDate.getTime())
          ? 'Unknown date'
          : fallbackDate.toDateString();
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(match);
    });
    return grouped;
  };

  const formatGroupDateLabel = (dateKey: string) => {
    const d = new Date(dateKey);
    if (Number.isNaN(d.getTime())) return dateKey || 'Unknown date';
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleBatchPrediction = (matchId: string, predictedWinner: string) => {
    // If there's a saved prediction and user is selecting a different team, 
    // this will override the saved prediction with the new selection
    setBatchPredictions(prev => ({
      ...prev,
      [matchId]: predictedWinner
    }));
  };

  // Enhanced prediction handlers
  const handleTotalRunsPrediction = (matchId: string, totalRuns: number) => {
    setTotalRunsPredictions(prev => ({
      ...prev,
      [matchId]: totalRuns
    }));
  };

  const handleMomPrediction = (matchId: string, playerId: string | number, playerName: string) => {
    console.log('🔍 handleMomPrediction called with:', { matchId, playerId, playerName, type: typeof playerId });
    console.log('🔍 Current momPredictions state:', momPredictions);
    
    setMomPredictions(prev => {
      const newPredictions = { ...prev };
      newPredictions[matchId] = { id: playerId, name: playerName };
      console.log('🔍 Updated momPredictions:', newPredictions);
      return newPredictions;
    });
  };

  // Handle rename league
  const handleRenameLeague = async (newName: string) => {
    if (!league || !newName.trim()) return;
    
    setIsRenaming(true);
    try {
      const result = await apiService.updatePickemLeagueName(league.id, newName);
      if (result.success) {
        setLeague({ ...league, name: newName });
        setEditedLeagueName(newName);
        setIsEditingName(false);
      } else {
        alert(result.message || 'Failed to update league name');
        setEditedLeagueName(league.name);
      }
    } catch (error: any) {
      console.error('Error renaming league:', error);
      alert(error.message || 'Failed to update league name');
      setEditedLeagueName(league.name);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleUpdateMaxParticipants = async (newMax: number) => {
    if (!league || newMax === league.max_participants || newMax < league.current_participants) {
      return;
    }

    setIsUpdatingMaxParticipants(true);
    try {
      const result = await apiService.updateFantasyLeagueMaxParticipants(league.id, newMax);
      if (result.success) {
        setLeague({ ...league, max_participants: newMax });
        setEditedMaxParticipants(newMax);
        setIsEditingMaxParticipants(false);
        setNotification('Max participants updated successfully!');
        setTimeout(() => setNotification(null), 3000);
      } else {
        setNotification(result.message || 'Failed to update max participants');
        setTimeout(() => setNotification(null), 3000);
        setEditedMaxParticipants(league.max_participants);
      }
    } catch (err: any) {
      console.error('Error updating max participants:', err);
      setNotification(err.message || 'Failed to update max participants');
      setTimeout(() => setNotification(null), 3000);
      setEditedMaxParticipants(league.max_participants);
    } finally {
      setIsUpdatingMaxParticipants(false);
    }
  };

  const loadMatchSquadPlayers = async (matchId: string) => {
    try {
      console.log('🔍 Loading squad players for match:', matchId);
      
      // Check if we already have squad data in the match
      const match = matches.find(m => m.id === matchId);
      if (match && match.squad_players && match.squad_players.length > 0) {
        console.log('🔍 Using squad data from match:', match.squad_players.length, 'players');
        setMatchSquadPlayers(prev => ({
          ...prev,
          [matchId]: match.squad_players!
        }));
        return;
      }
      
      // Check if we already have squad data in state
      if (matchSquadPlayers[matchId] && matchSquadPlayers[matchId].length > 0) {
        console.log('🔍 Using existing squad data from state:', matchSquadPlayers[matchId].length, 'players');
        return;
      }
      
      // Fallback to API call if squad data not available
      console.log('🔍 Squad data not available, fetching from API');
      const leagueId = parseInt(id!);
      const playersData = await apiService.getMatchSquadPlayers(leagueId, matchId);
      console.log('🔍 API returned players:', playersData.players?.length || 0);
      
      if (playersData.players && playersData.players.length > 0) {
      setMatchSquadPlayers(prev => ({
        ...prev,
        [matchId]: playersData.players
      }));
      } else {
        console.warn('🔍 No squad players returned from API for match:', matchId);
        setMatchSquadPlayers(prev => ({
          ...prev,
          [matchId]: []
        }));
      }
    } catch (err: any) {
      console.error('Failed to load squad players for match', matchId, ':', err);
      // Set empty array to prevent infinite loading
      setMatchSquadPlayers(prev => ({
        ...prev,
        [matchId]: []
      }));
    }
  };

  const loadLeagueSettings = async () => {
    try {
      const leagueId = parseInt(id!);
      const settingsData = await apiService.getLeagueSettings(leagueId);
      setLeagueSettings(settingsData.settings);
    } catch (err: any) {
      console.error('Failed to load league settings:', err);
    }
  };

  // Ensure squad data is loaded for a match when needed
  const ensureSquadDataLoaded = async (matchId: string) => {
    if (!matchSquadPlayers[matchId] || matchSquadPlayers[matchId].length === 0) {
      await loadMatchSquadPlayers(matchId);
    }
  };

  const handleSaveBatchPredictions = async () => {
    const hasTeamPredictions = Object.keys(batchPredictions).length > 0;
    const hasTotalRunsPredictions = Object.keys(totalRunsPredictions).length > 0;
    const hasMomPredictions = Object.keys(momPredictions).length > 0;
    const hasWinMarginPredictions = Object.keys(winMarginPredictions).length > 0;
    
    console.log('🔍 Before saving - Batch state:', {
      batchPredictions: Object.keys(batchPredictions).length,
      totalRunsPredictions: Object.keys(totalRunsPredictions).length,
      momPredictions: Object.keys(momPredictions).length,
      winMarginPredictions: Object.keys(winMarginPredictions).length
    });
    
    if (!hasTeamPredictions && !hasTotalRunsPredictions && !hasMomPredictions && !hasWinMarginPredictions) {
      setNotification('No predictions to save');
      return;
    }

    // Only save predictions for matches that have not started (avoid "match has started" errors)
    const entriesToSave = Object.entries(batchPredictions).filter(([matchId]) => {
      const match = matches.find(m => m.id === matchId);
      return match && !isMatchLockedLocal(match);
    });
    const skippedCount = Object.keys(batchPredictions).length - entriesToSave.length;

    if (entriesToSave.length === 0) {
      setNotification('No predictions to save — all selected matches have already started');
      return;
    }

    setSavingPredictions(true);
    try {
      const leagueId = parseInt(id!);
      const batchTransactionId = `batch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const promises = entriesToSave.map(([matchId, predictedWinner]) => {
        // Get enhanced prediction data
        const totalRuns = totalRunsPredictions[matchId] || undefined;
        const momPrediction = momPredictions[matchId];
        const momPlayerId = momPrediction?.id || undefined;
        const momPlayerName = momPrediction?.name || undefined;
        const winMarginPrediction = winMarginPredictions[matchId] || undefined;
        
        console.log('🔍 Saving prediction for match:', matchId, {
          predictedWinner,
          totalRuns,
          momPlayerId,
          momPlayerName,
          winMarginPrediction
        });
        
        return apiService.makePickemPrediction(
          leagueId,
          matchId,
          predictedWinner,
          totalRuns,
          momPlayerId,
          momPlayerName,
          winMarginPrediction,
          batchTransactionId
        );
      });
      
      await Promise.all(promises);
      console.log('🔍 All predictions saved successfully');
      
      // Reload predictions and standings
      const predictionsData = await apiService.getUserPickemPredictions(leagueId);
      console.log('🔍 Reloaded predictions from DB:', predictionsData.predictions);
      setPredictions(predictionsData.predictions);
      
      await refreshPickemStandings();
      
      // Instead of calling loadExistingPredictions, manually update the batch state
      // to preserve the current selections while ensuring they match the saved data
      console.log('🔍 Manually updating batch state with saved predictions');
      
      const updatedBatchPredictions = { ...batchPredictions };
      const updatedTotalRuns = { ...totalRunsPredictions };
      const updatedMomPredictions = { ...momPredictions };
      const updatedWinMarginPredictions = { ...winMarginPredictions };
      
      // Update batch state with confirmed saved data
      predictionsData.predictions.forEach((prediction: any) => {
        if (prediction.predicted_winner) {
          updatedBatchPredictions[prediction.match_id] = prediction.predicted_winner;
        }
        if (prediction.total_runs_prediction) {
          updatedTotalRuns[prediction.match_id] = prediction.total_runs_prediction;
        }
        if (prediction.mom_player_id && prediction.mom_player_name) {
          updatedMomPredictions[prediction.match_id] = {
            id: prediction.mom_player_id,
            name: prediction.mom_player_name
          };
        }
        if (prediction.win_margin_prediction) {
          updatedWinMarginPredictions[prediction.match_id] = prediction.win_margin_prediction;
        }
      });
      
      console.log('🔍 Updated batch states:', {
        updatedBatchPredictions,
        updatedTotalRuns,
        updatedMomPredictions,
        updatedWinMarginPredictions
      });
      
      // Set the updated states
      setBatchPredictions(updatedBatchPredictions);
      setTotalRunsPredictions(updatedTotalRuns);
      setMomPredictions(updatedMomPredictions);
      setWinMarginPredictions(updatedWinMarginPredictions);
      
      console.log('🔍 Batch state updated - UI should now show saved predictions');
      
      const savedCount = entriesToSave.length;
      setNotification(
        skippedCount > 0
          ? `Saved ${savedCount} prediction${savedCount !== 1 ? 's' : ''}. ${skippedCount} match${skippedCount !== 1 ? 'es' : ''} had already started and ${skippedCount === 1 ? 'was' : 'were'} skipped.`
          : 'All predictions saved successfully!'
      );
      if (showLeagueActivity) {
        loadUserActivity();
      }

      void requestPickConfirmationEmail(batchTransactionId);

      setTimeout(() => setNotification(null), 3000);
    } catch (err: any) {
      console.error('🔍 Error saving predictions:', err);
      setError(err.message || 'Failed to save predictions');
    } finally {
      setSavingPredictions(false);
    }
  };

  const handlePickConfirmationToggle = async (enabled: boolean) => {
    if (!id || currentUserId == null) return;
    setPickConfirmationSaving(true);
    try {
      const res: any = await apiService.updatePickemPickConfirmationEmail(parseInt(id, 10), enabled);
      const on = !!res.pickem_email_prediction_confirmation;
      setParticipants((prev) =>
        prev.map((p) => (p.user_id === currentUserId ? { ...p, pickem_email_prediction_confirmation: on } : p))
      );
    } catch (err: any) {
      setError(err.message || 'Could not update email preference');
    } finally {
      setPickConfirmationSaving(false);
    }
  };

  const getMatchDisplayName = (match: Match, index: number) => {
    // Use the backend-provided name which already has correct match numbering
    return match.name;
  };

  const getCurrentPrediction = (matchId: string) => {
    // Prioritize batch predictions (current selection) over saved predictions
    const current = batchPredictions[matchId] || '';
    return current;
  };

  const getSavedPrediction = (matchId: string) => {
    // Get the saved prediction from the database
    const existingPrediction = predictions.find(p => p.match_id === matchId);
    return existingPrediction?.predicted_winner || '';
  };

  // Helper functions for All Picks tab
  /** True when tournament_match_results has an admin-set terminal outcome (not only "completed"). */
  const matchCountsAsSettledForAllPicksDefault = (match: Match): boolean => {
    const label = getMatchStatusLocal(match);
    if (label === 'Live' || label === 'Completed' || label === 'Abandoned') return true;
    const raw = (match.status || '').toLowerCase().trim();
    const terminalAdminStatuses = new Set([
      'tie',
      'draw',
      'tied',
      'no_result',
      'nr',
      'cancelled',
      'canceled',
      'washout'
    ]);
    if (terminalAdminStatuses.has(raw)) return true;
    return false;
  };

  const getCurrentMatch = () => {
    const nowGMT = getCurrentGMTTime();
    return matches
      .filter(match => {
        const matchDateGMT = parseMatchDateToGMT(match.match_date, match);
        return matchDateGMT ? matchDateGMT <= nowGMT && matchCountsAsSettledForAllPicksDefault(match) : false;
      })
      .sort((a, b) => {
        const dateA = parseMatchDateToGMT(a.match_date, a);
        const dateB = parseMatchDateToGMT(b.match_date, b);
        return dateA && dateB ? dateA.getTime() - dateB.getTime() : 0;
      })
      .pop();
  };

  const getNextMatch = () => {
    // Find the next upcoming match
    const nowGMT = getCurrentGMTTime();
    return matches
      .filter(match => {
        const matchDateGMT = parseMatchDateToGMT(match.match_date, match);
        return matchDateGMT ? matchDateGMT > nowGMT && !isMatchLockedLocal(match) : false;
      })
      .sort((a, b) => {
        const dateA = parseMatchDateToGMT(a.match_date, a);
        const dateB = parseMatchDateToGMT(b.match_date, b);
        return dateA && dateB ? dateA.getTime() - dateB.getTime() : 0;
      })
      .shift(); // Get the first upcoming one
  };

  const getActiveMatch = () => {
    // Get current match if it exists, otherwise get next match
    const currentMatch = getCurrentMatch();
    if (currentMatch) return currentMatch;
    
    return getNextMatch();
  };

  // Check if league has started (first match after league creation has begun)
  const checkIfLeagueHasStarted = () => {
    if (!league || !matches.length) return false;
    
    const leagueCreatedAt = new Date(league.created_at || new Date());
    
    // Find the first match that started after the league was created
    const matchesAfterLeagueCreation = matches.filter(match => {
      const matchDateGMT = parseMatchDateToGMT(match.match_date, match);
      return matchDateGMT ? matchDateGMT >= leagueCreatedAt : false;
    }).sort((a, b) => {
      const dateA = parseMatchDateToGMT(a.match_date, a);
      const dateB = parseMatchDateToGMT(b.match_date, b);
      return dateA && dateB ? dateA.getTime() - dateB.getTime() : 0;
    });
    
    if (matchesAfterLeagueCreation.length === 0) return false;
    
    // Check if the first match after league creation has started
    const firstMatchAfterCreation = matchesAfterLeagueCreation[0];
    const nowGMT = getCurrentGMTTime();
    const matchDateGMT = parseMatchDateToGMT(firstMatchAfterCreation.match_date, firstMatchAfterCreation);
    
    // League has started if the first match after creation has passed its GMT start time
    return matchDateGMT ? nowGMT >= matchDateGMT : false;
  };

  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(`${type} copied!`);
      setTimeout(() => setCopyFeedback(null), 2000);
    } catch (err) {
      setCopyFeedback('Failed to copy');
      setTimeout(() => setCopyFeedback(null), 2000);
    }
  };

  const handleLeaveLeague = async () => {
    try {
      const leagueId = parseInt(id!);
      await apiService.leaveFantasyLeague(leagueId);
      setNotification('Successfully left the league');
      setTimeout(() => {
        navigate('/tournaments');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to leave league');
    }
  };

  const handleLeagueDeleted = () => {
    setNotification('League deleted successfully');
    setTimeout(() => {
      navigate('/tournaments');
    }, 2000);
  };



  // Helper to render points breakdown for a prediction category
  const renderPointsBreakdown = (label: string, value: any, breakdown: any, category: string) => {
    if (!breakdown) return null;
    const cat = breakdown[category];
    if (!cat) return null;
    const isCorrect = cat.correct;
    const points = cat.points;
    const displayLabel =
      category === 'total_runs'
        ? (cat.category === 'exact' ? 'Exact' : cat.category === 'close' ? 'Close' : cat.category === 'medium' ? 'Medium' : 'None')
        : isCorrect ? 'Correct' : 'Wrong';
    const colorClass = isCorrect && points > 0 ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
    return (
      <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${colorClass}`}
        title={displayLabel}
      >
        {displayLabel} - {points}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-accent border-t-transparent mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-white mb-2">Loading League</h2>
          <p className="text-blue-100">Please wait while we fetch the latest data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-bold text-red-400 mb-2">Oops!</h2>
            <p className="text-red-300 mb-4">{error}</p>
          </div>
          <button
            onClick={() => navigate('/my-leagues')}
            className="bg-accent text-primary font-bold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            ← Back to Leagues
          </button>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">League Not Found</h2>
          <button
            onClick={() => navigate('/my-leagues')}
            className="bg-accent text-primary font-bold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            ← Back to Leagues
          </button>
        </div>
      </div>
    );
  }

  return (
    <>

      <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7]">
      {/* Copy Feedback */}
      {copyFeedback && (
        <div className="fixed top-4 right-4 z-50 bg-accent text-primary px-4 py-2 rounded-lg shadow-lg border border-accent/20 animate-in slide-in-from-right duration-300">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">{copyFeedback}</span>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className="fixed top-4 left-4 z-50 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg border border-blue-400 animate-in slide-in-from-left duration-300">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium">{notification}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary p-2 sm:p-4 md:p-5">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate('/my-leagues')}
                className="text-blue-100 hover:text-white transition-colors duration-200 mb-1 sm:mb-2 flex items-center text-xs sm:text-sm"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="hidden sm:inline">Back to Leagues</span>
                <span className="sm:hidden">Back</span>
              </button>
              <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-accent mb-1 truncate">{league.name}</h1>
              <p className="text-blue-100 text-xs sm:text-sm md:text-base">
                <span className="truncate block sm:inline">{league.tournament_name}</span>
                <span className="hidden sm:inline"> • </span>
                <span className="block sm:inline">{league.format} • {league.year}</span>
              </p>
            </div>
            <div className="text-left sm:text-right flex-shrink-0">
              <div className="inline-flex items-center space-x-1 sm:space-x-2">
                <span className="text-blue-100 text-xs sm:text-sm">Participants:</span>
                <span className="text-white text-xs sm:text-sm md:text-base font-semibold">{league.current_participants}/{league.max_participants}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-3 sm:py-4 md:py-5">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-1 border border-white/20 mb-6 sm:mb-8 min-w-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
            {[
              { key: 'info', label: 'League Info', icon: '🏆', shortLabel: 'Info' },
              { key: 'picks', label: 'My Picks', icon: '🎯', shortLabel: 'Picks' },
              { key: 'all-picks', label: 'All Picks', icon: '👥', shortLabel: 'All' },
              { key: 'standings', label: 'Standings', icon: '📊', shortLabel: 'Standings' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key as MainTab)}
                className={`py-2 sm:py-2.5 px-2 sm:px-3 md:px-4 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 text-center min-w-0 ${
                  activeTab === tab.key
                    ? 'bg-accent text-primary shadow-lg ring-2 ring-white/30 sm:ring-0 sm:shadow-lg'
                    : 'text-white hover:text-accent hover:bg-white/10'
                }`}
              >
                <span className="mr-1 sm:mr-2">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-8 min-w-0 max-w-full">
          {activeTab === 'info' && (
            <>
            <div className="grid md:grid-cols-2 gap-8">
              {/* League Details */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-primary flex items-center">
                    <span className="mr-3">🏆</span>
                    League Details
                  </h2>
                  <button
                    onClick={() => setShowSettingsModal(true)}
                    className="p-2 text-gray-600 hover:text-accent hover:bg-gray-100 rounded-lg transition-colors"
                    title="Open League Settings"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">League Name:</span>
                    <div className="flex items-center space-x-2">
                      {isEditingName ? (
                        <>
                          <input
                            type="text"
                            value={editedLeagueName}
                            onChange={(e) => setEditedLeagueName(e.target.value)}
                            className="px-2 py-1 bg-gray-50 border border-gray-300 rounded text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-accent"
                            autoFocus
                          />
                          <button
                            onClick={async () => {
                              if (editedLeagueName.trim() && editedLeagueName.trim() !== league.name) {
                                await handleRenameLeague(editedLeagueName.trim());
                              }
                              setIsEditingName(false);
                            }}
                            disabled={isRenaming || !editedLeagueName.trim() || editedLeagueName.trim() === league.name}
                            className="text-green-600 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Save"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingName(false);
                              setEditedLeagueName(league.name);
                            }}
                            className="text-red-600 hover:text-red-700 transition-colors"
                            title="Cancel"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="text-primary font-semibold">{league.name}</span>
                          {isManager && (
                            <button
                              onClick={() => {
                                setEditedLeagueName(league.name);
                                setIsEditingName(true);
                              }}
                              className="text-accent hover:text-accent/80 transition-colors ml-2"
                              title="Edit league name"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Tournament:</span>
                    <span className="text-primary font-semibold">{league.tournament_name}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Format:</span>
                    <span className="text-primary font-semibold">{league.format}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Year:</span>
                    <span className="text-primary font-semibold">{league.year}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      league.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : league.status === 'completed'
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {league.status.charAt(0).toUpperCase() + league.status.slice(1)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Type:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      league.is_public 
                        ? 'bg-secondary/20 text-secondary'
                        : 'bg-accent/20 text-accent'
                    }`}>
                      {league.is_public ? 'Public' : 'Private'}
                    </span>
                  </div>
                  {league.join_code && (
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600">Join Code:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-primary font-mono bg-gray-100 px-3 py-1 rounded border border-gray-200">
                          {league.join_code}
                        </span>
                        <button
                          onClick={() => handleCopyToClipboard(league.join_code!, 'Join code')}
                          className="text-accent hover:text-accent/80 transition-colors duration-200"
                          title="Copy join code"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  {league.passcode && (
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600">League Passcode:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-primary font-mono bg-gray-100 px-3 py-1 rounded border border-gray-200">
                          {league.passcode}
                        </span>
                        <button
                          onClick={() => handleCopyToClipboard(league.passcode!, 'Passcode')}
                          className="text-accent hover:text-accent/80 transition-colors duration-200"
                          title="Copy passcode"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  {league.created_at && (
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600">Created:</span>
                      <span className="text-primary font-medium">{formatDate(league.created_at)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Participants:</span>
                    <div className="flex items-center space-x-2">
                      {isEditingMaxParticipants ? (
                        <>
                          <span className="text-primary font-semibold">{league.current_participants}/</span>
                          <input
                            type="number"
                            value={editedMaxParticipants}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              if (!isNaN(val) && val >= league.current_participants) {
                                setEditedMaxParticipants(val);
                              }
                            }}
                            min={league.current_participants}
                            className="w-20 px-2 py-1 bg-gray-50 border border-gray-300 rounded text-primary font-semibold focus:outline-none focus:ring-2 focus:ring-accent"
                            autoFocus
                          />
                          <button
                            onClick={async () => {
                              if (editedMaxParticipants !== league.max_participants && editedMaxParticipants >= league.current_participants) {
                                await handleUpdateMaxParticipants(editedMaxParticipants);
                              }
                            }}
                            disabled={isUpdatingMaxParticipants || editedMaxParticipants === league.max_participants || editedMaxParticipants < league.current_participants}
                            className="text-green-600 hover:text-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Save"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingMaxParticipants(false);
                              setEditedMaxParticipants(league.max_participants);
                            }}
                            className="text-red-600 hover:text-red-700 transition-colors"
                            title="Cancel"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="text-primary font-semibold">{league.current_participants}/{league.max_participants}</span>
                          {isManager && (
                            <button
                              onClick={() => {
                                setEditedMaxParticipants(league.max_participants);
                                setIsEditingMaxParticipants(true);
                              }}
                              className="text-accent hover:text-accent/80 transition-colors ml-2"
                              title="Edit max participants"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {participants.find(p => p.role === 'manager') && (
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600">League Manager:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-primary font-medium">
                          {participants.find(p => p.role === 'manager')?.display_name}
                        </span>
                        <span className="px-2 py-1 bg-accent text-primary text-xs font-medium rounded-full">
                          Manager
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Share Information */}
                {(league.join_code || league.passcode) && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-primary font-semibold mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                      </svg>
                      Share League
                    </h4>
                    <p className="text-gray-600 text-sm mb-3">
                      Share this link with friends. They'll be able to join after logging in or signing up.
                    </p>
                    {(() => {
                      const shareUrl = `${window.location.origin}/join-league?code=${league.join_code || ''}&passcode=${league.passcode || ''}&type=pickem`;
                      return (
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              readOnly
                              value={shareUrl}
                              className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded text-gray-800 text-sm font-mono"
                            />
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(shareUrl);
                                alert('Link copied to clipboard!');
                              }}
                              className="px-4 py-2 bg-accent hover:bg-accent/90 text-primary rounded transition-colors flex items-center space-x-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              <span>Copy Link</span>
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {league.join_code && (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                Join Code: {league.join_code}
                              </span>
                            )}
                            {league.passcode && (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                Passcode: {league.passcode}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Participants */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <h2 className="text-2xl font-bold text-primary mb-6 flex items-center">
                  <span className="mr-3">👥</span>
                  Participants ({participants.length})
                </h2>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {participants.map((participant, index) => (
                    <div key={participant.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-primary font-medium">{participant.display_name}</p>
                          <p className="text-gray-500 text-sm">{formatDate(participant.joined_at)}</p>
                        </div>
                      </div>
                      {participant.role === 'manager' && (
                        <span className="px-2 py-1 bg-accent text-primary text-xs font-medium rounded-full">
                          Manager
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Settings Section replaced by modal */}
            </div>

            {/* Email confirmation opt-in (per member, this league) */}
            {currentUserId != null &&
              participants.some((p) => p.user_id === currentUserId) && (
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 mt-6 sm:mt-8">
                  <h3 className="text-base font-semibold text-primary mb-2 flex items-center gap-2">
                    <span aria-hidden>✉️</span>
                    Pick save emails
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    When enabled, we send <strong>you</strong> one email per save (including batch saves as a single
                    message) listing the same prediction changes as in &quot;Your prediction activity&quot;.                     Time
                    stamps show <strong>UTC</strong> and your browser&apos;s local zone. A single archive copy may be
                    sent to the site operations inbox (not league managers or admin accounts) for support records.
                  </p>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={!!participants.find((p) => p.user_id === currentUserId)?.pickem_email_prediction_confirmation}
                      disabled={pickConfirmationSaving}
                      onChange={(e) => void handlePickConfirmationToggle(e.target.checked)}
                    />
                    <span className="text-sm text-gray-800">
                      Email me a confirmation when I save picks in this league
                      {pickConfirmationSaving ? <span className="text-gray-500"> (saving…)</span> : null}
                    </span>
                  </label>
                </div>
              )}

            {/* User prediction activity (league-scoped, user-only) */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mt-6 sm:mt-8">
              <button
                type="button"
                onClick={() => setShowLeagueActivity((open) => !open)}
                className="w-full flex items-center justify-between px-4 sm:px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                aria-expanded={showLeagueActivity}
              >
                <span className="text-base sm:text-lg font-semibold text-primary flex items-center gap-2">
                  <span aria-hidden>📝</span>
                  Your prediction activity
                  <span className="text-xs font-normal text-gray-500 hidden sm:inline">(this league only)</span>
                </span>
                <svg
                  className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${showLeagueActivity ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showLeagueActivity && (
                <div className="px-4 sm:px-6 pb-6 pt-0 border-t border-gray-100">
                  <div className="space-y-4 pt-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-600">Recent prediction transactions</h3>
                      <button
                        type="button"
                        onClick={loadUserActivity}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Refresh
                      </button>
                    </div>
                    {userActivityLoading && <p className="text-sm text-gray-500">Loading your activity...</p>}
                    {userActivityError && !userActivityLoading && (
                      <p className="text-sm text-red-600">{userActivityError}</p>
                    )}
                    {!userActivityLoading && !userActivityError && userActivity.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <p>No prediction activity yet.</p>
                        <p className="text-sm">Each create/update transaction for this league will appear here.</p>
                      </div>
                    )}
                    {!userActivityLoading && !userActivityError && userActivity.length > 0 && (
                      <div className="space-y-3">
                        {userActivity.map((ev) => {
                          const nextTeam = ev.new_predicted_winner?.trim() || '—';
                          const oldTeam = ev.old_predicted_winner?.trim() || '—';
                          const label =
                            ev.event_type === 'created'
                              ? `Prediction made: ${nextTeam}`
                              : ev.event_type === 'cleared'
                                ? 'Prediction cleared'
                                : `Prediction updated: ${oldTeam} → ${nextTeam}`;
                          const when = new Date(ev.created_at).toLocaleString();
                          const matchLabel = ev.match_name || `Match ${ev.match_id}`;
                          const matchDate = ev.match_date ? new Date(ev.match_date).toLocaleString() : null;
                          return (
                            <div key={ev.id} className="flex items-start space-x-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0">
                                🎯
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-primary font-medium">{label}</p>
                                <p className="text-gray-700 text-sm">{matchLabel}</p>
                                <p className="text-gray-500 text-xs">
                                  Saved: {when}
                                  {matchDate ? ` • Match start: ${matchDate}` : ''}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            </>
          )}

          {activeTab === 'picks' && (
            <div className="space-y-6">
              <div className="flex flex-row justify-between items-center gap-3">
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white flex items-center">
                  <span className="mr-2 sm:mr-3">🎯</span>
                  <span className="text-lg sm:text-xl md:text-2xl">My Predictions</span>
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('dropdown')}
                    className={`px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 ${
                      viewMode === 'dropdown' 
                        ? 'bg-accent text-primary' 
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    Single Match
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 ${
                      viewMode === 'list' 
                        ? 'bg-accent text-primary' 
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    All Matches
                  </button>
                </div>
              </div>

              {/* Notification */}
              {notification && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800">{notification}</p>
                </div>
              )}

              {viewMode === 'dropdown' ? (
                <div className="space-y-6">
                  {/* Match Selection Dropdown */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-primary">Select Match</h3>
                      {selectedMatchId && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Next Match
                        </span>
                      )}
                    </div>
                    <select
                      value={selectedMatchId || ''}
                      onChange={(e) => setSelectedMatchId(e.target.value || null)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                    >
                      <option value="">
                        {getAllMatchesForDropdown().length === 0 ? 'No matches available' : 'Choose a match...'}
                      </option>
                      {getAllMatchesForDropdown().map((match, index) => {
                        const matchStatus = getMatchStatusLocal(match);
                        // For abandoned matches, don't treat them as completed for prediction display
                        const isCompleted = (matchStatus === 'Completed' || isMatchLockedLocal(match)) && matchStatus !== 'Abandoned';
                        const hasPrediction = getPredictionForMatch(match.id);
                        const prediction = hasPrediction;
                        const isCorrect = prediction && prediction.is_correct;
                        
                        // Debug logging
                        console.log('🔍 Dropdown match status:', {
                          matchId: match.id,
                          matchName: match.name,
                          matchStatus: matchStatus,
                          dbStatus: match.status,
                          isCompleted: isCompleted,
                          hasWinner: !!match.winner,
                          winner: match.winner
                        });
                        
                        return (
                        <option key={match.id} value={match.id}>
                          {match.name} - {formatDate(match.match_date)}
                            {matchStatus === 'Completed' ? ' (Completed)' : 
                             matchStatus === 'Abandoned' ? ' (Abandoned)' :
                             matchStatus === 'Started' ? ' (Started)' : 
                             matchStatus === 'Live' ? ' (Live)' : ' (Upcoming)'}
                            {hasPrediction && isCompleted && (
                              isCorrect ? ' ✅' : ' ❌'
                            )}
                        </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* No Upcoming Matches Message */}
                  {getUpcomingMatches().length === 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
                      <div className="text-yellow-800">
                        <svg className="w-12 h-12 mx-auto mb-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="text-lg font-semibold mb-2">No Upcoming Matches</h3>
                        <p className="text-yellow-700">All matches in this tournament have been completed or are currently in progress.</p>
                      </div>
                    </div>
                  )}

                  {/* Selected Match Prediction */}
                  {selectedMatchId && (() => {
                    const match = matches.find(m => String(m.id) === String(selectedMatchId));
                    const prediction = getPredictionForMatch(selectedMatchId);
                    const isCompleted = match ? getMatchStatusLocal(match) === 'Completed' || isMatchLockedLocal(match) : false;
                    // For completed matches, use the saved prediction; for upcoming matches, use current batch prediction
                    const currentPrediction = isCompleted ? prediction?.predicted_winner || '' : getCurrentPrediction(selectedMatchId);
                    // Derive correct from winner vs predicted_winner (trim + case-insensitive) so display is right even if DB is_correct was stale
                    const isCorrect = prediction && (
                      prediction.winner && prediction.predicted_winner
                        ? String(prediction.predicted_winner).trim().toLowerCase() === String(prediction.winner).trim().toLowerCase()
                        : !!prediction.is_correct
                    );
                    
                    if (!match) return null;
                    
                    return (
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-bold text-primary">
                            {match.name}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            getMatchStatusLocal(match) === 'Completed'
                              ? isCorrect 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                              : getMatchStatusLocal(match) === 'Abandoned'
                              ? 'bg-gray-100 text-gray-800'
                              : getMatchStatusLocal(match) === 'Started'
                              ? 'bg-orange-100 text-orange-800'
                              : getMatchStatusLocal(match) === 'Live'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {getMatchStatusLocal(match) === 'Completed' 
                              ? (isCorrect ? 'Correct ✅' : 'Incorrect ❌') 
                              : getMatchStatusLocal(match) === 'Abandoned'
                              ? 'Abandoned'
                              : getMatchStatusLocal(match) === 'Started'
                              ? 'Started ⏳'
                              : getMatchStatusLocal(match) === 'Live'
                              ? 'Live'
                              : 'Upcoming'}
                          </span>
                        </div>
                        

                        
                        <div className="text-gray-600 text-sm mb-6 space-y-1">
                          <p>📅 Date: {formatDate(match.match_date)}</p>
                          <p>🏟️ Venue: {pickemVenueDisplayLabel(match.venue)}</p>
                        </div>

                        <div className="space-y-6">
                          <h4 className="text-primary font-semibold text-center">
                            {isCompleted ? 'Your Prediction & Result:' : 'Select Your Pick:'}
                          </h4>
                          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                            <button
                              onClick={() => !isCompleted && handleBatchPrediction(match.id, match.team1_name)}
                              disabled={isCompleted}
                              className={`flex items-center justify-center space-x-2 sm:space-x-3 py-2 sm:py-3 md:py-4 px-3 sm:px-4 md:px-6 rounded-lg border transition-all duration-300 relative ${
                                isCompleted
                                  ? currentPrediction === match.team1_name
                                    ? isCorrect
                                      ? 'border-green-500 bg-green-100 shadow-lg'
                                      : 'border-red-500 bg-red-100 shadow-lg'
                                    : match.winner === match.team1_name
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-300 bg-gray-50'
                                  : currentPrediction === match.team1_name
                                  ? 'border-yellow-500 bg-yellow-200 shadow-lg transform scale-105 hover:bg-gray-50'
                                  : 'border-gray-300 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <span className={`text-lg font-semibold ${
                                isCompleted
                                  ? currentPrediction === match.team1_name
                                    ? isCorrect
                                      ? 'text-green-800'
                                      : 'text-red-800'
                                    : match.winner === match.team1_name
                                    ? 'text-green-700'
                                    : 'text-gray-600'
                                  : currentPrediction === match.team1_name 
                                  ? 'text-yellow-800'
                                  : 'text-primary'
                              }`}>{match.team1_name}</span>
                              {isCompleted && match.winner === match.team1_name && (
                                <span className="text-green-600 font-bold">🏆</span>
                              )}
                              {!isCompleted && (getSavedPrediction(match.id) === match.team1_name || currentPrediction === match.team1_name) && (
                                <span className="text-green-600">
                                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </button>
                            <button
                              onClick={() => !isCompleted && handleBatchPrediction(match.id, match.team2_name)}
                              disabled={isCompleted}
                              className={`flex items-center justify-center space-x-2 sm:space-x-3 py-2 sm:py-3 md:py-4 px-3 sm:px-4 md:px-6 rounded-lg border transition-all duration-300 relative ${
                                isCompleted
                                  ? currentPrediction === match.team2_name
                                    ? isCorrect
                                      ? 'border-green-500 bg-green-100 shadow-lg'
                                      : 'border-red-500 bg-red-100 shadow-lg'
                                    : match.winner === match.team2_name
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-300 bg-gray-50'
                                  : currentPrediction === match.team2_name
                                  ? 'border-yellow-500 bg-yellow-200 shadow-lg transform scale-105 hover:bg-gray-50'
                                  : 'border-gray-300 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <span className={`text-sm sm:text-base md:text-lg font-semibold ${
                                isCompleted
                                  ? currentPrediction === match.team2_name
                                    ? isCorrect
                                      ? 'text-green-800'
                                      : 'text-red-800'
                                    : match.winner === match.team2_name
                                    ? 'text-green-700'
                                    : 'text-gray-600'
                                  : currentPrediction === match.team2_name 
                                  ? 'text-yellow-800'
                                  : 'text-primary'
                              }`}>{match.team2_name}</span>
                              {isCompleted && match.winner === match.team2_name && (
                                <span className="text-green-600 font-bold text-sm sm:text-base">🏆</span>
                              )}
                              {!isCompleted && (getSavedPrediction(match.id) === match.team2_name || currentPrediction === match.team2_name) && (
                                <span className="text-green-600">
                                  <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </button>
                          </div>

                          {!isCompleted && league?.dynamic_scoring_enabled && id && (
                            <DynamicPickScoringNote leagueId={parseInt(id, 10)} matchId={String(match.id)} />
                          )}
                          
                          {/* Match Result for Completed Matches */}
                          {isCompleted && prediction && (() => {
                            console.log('🔍 Match result debug - prediction:', prediction);
                            console.log('🔍 Match result debug - points_breakdown:', prediction.points_breakdown);
                            console.log('🔍 Match result debug - points_breakdown structure:', {
                              match_winner: prediction.points_breakdown?.match_winner,
                              total_runs: prediction.points_breakdown?.total_runs,
                              man_of_match: prediction.points_breakdown?.man_of_match,
                              streak_bonus: prediction.points_breakdown?.streak_bonus,
                              streak_penalty: prediction.points_breakdown?.streak_penalty
                            });
                            return (
                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                              <h5 className="text-gray-800 font-semibold mb-3 flex items-center">
                                <span className="mr-2">📊</span>
                                Match Results Summary
                              </h5>
                              <div className="overflow-x-auto -mx-2 sm:mx-0">
                                <table className="w-full text-xs sm:text-sm min-w-[500px]">
                                  <thead>
                                    <tr className="border-b border-gray-200">
                                      <th className="text-left py-2 px-2 sm:px-3 font-semibold text-gray-700 whitespace-nowrap">Your Predictions</th>
                                      <th className="text-left py-2 px-2 sm:px-3 font-semibold text-gray-700 whitespace-nowrap">Match Results</th>
                                      <th className="text-center py-2 px-2 sm:px-3 font-semibold text-gray-700 whitespace-nowrap">Correct?</th>
                                      <th className="text-center py-2 px-2 sm:px-3 font-semibold text-gray-700 whitespace-nowrap">Points</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {/* Team Winner Prediction */}
                                    <tr className="border-b border-gray-100">
                                      <td className="py-2 px-2 sm:px-3">
                                        <span className="font-medium text-xs sm:text-sm">{prediction.predicted_winner}</span>
                                      </td>
                                      <td className="py-2 px-2 sm:px-3">
                                        <span className="font-medium text-xs sm:text-sm">{prediction.winner || 'Not available'}</span>
                                      </td>
                                      <td className="py-2 px-2 sm:px-3 text-center">
                                        {prediction.is_correct ? (
                                          <span className="text-green-600 font-bold">✓</span>
                                        ) : (
                                          <span className="text-red-600 font-bold">✗</span>
                                        )}
                                      </td>
                                      <td className="py-2 px-2 sm:px-3 text-center">
                                        <span className="font-medium text-blue-700 text-xs sm:text-sm">
                                          {prediction.points_breakdown?.match_winner?.points || 0}
                                        </span>
                                      </td>
                                    </tr>
                                    
                                    {/* Total Runs Prediction - show if league has it or user made a prediction */}
                                    {((leagueSettings?.total_runs_exact_prediction_enabled === 'true' || 
                                      leagueSettings?.total_runs_close_prediction_enabled === 'true' || 
                                      leagueSettings?.total_runs_medium_prediction_enabled === 'true') ||
                                      (prediction.total_runs_prediction != null)) && (
                                      <tr className="border-b border-gray-100">
                                        <td className="py-2 px-2 sm:px-3">
                                          <span className="font-medium text-xs sm:text-sm">
                                            {prediction.total_runs_prediction ? `${prediction.total_runs_prediction} runs` : 'Not predicted'}
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 sm:px-3">
                                          <span className="font-medium text-xs sm:text-sm">
                                            {prediction.total_runs_result || prediction.tournament_total_runs || 'Not available'}
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 sm:px-3 text-center">
                                          {prediction.total_runs_prediction ? (
                                            prediction.points_breakdown?.total_runs?.correct ? (
                                              <span className="text-green-600 font-bold">✓</span>
                                            ) : (
                                              <span className="text-red-600 font-bold">✗</span>
                                            )
                                          ) : (
                                            <span className="text-gray-400">-</span>
                                          )}
                                        </td>
                                        <td className="py-2 px-2 sm:px-3 text-center">
                                          <span className="font-medium text-blue-700 text-xs sm:text-sm">
                                            {prediction.points_breakdown?.total_runs?.points || 0}
                                          </span>
                                        </td>
                                      </tr>
                                    )}
                                    
                                    {/* Man of Match Prediction - show if league has it or user made a prediction */}
                                    {(leagueSettings?.man_of_match_prediction_enabled === 'true' || (prediction.mom_player_name && prediction.mom_player_name.trim() !== '')) && (
                                      <tr className="border-b border-gray-100">
                                        <td className="py-2 px-2 sm:px-3">
                                          <span className="font-medium text-xs sm:text-sm">
                                            {prediction.mom_player_name || 'Not predicted'}
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 sm:px-3">
                                          <span className="font-medium text-xs sm:text-sm">
                                            {prediction.mom_result || prediction.tournament_man_of_match || 'Not available'}
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 sm:px-3 text-center">
                                          {prediction.mom_player_name ? (
                                            prediction.points_breakdown?.man_of_match?.correct ? (
                                              <span className="text-green-600 font-bold">✓</span>
                                            ) : (
                                              <span className="text-red-600 font-bold">✗</span>
                                            )
                                          ) : (
                                            <span className="text-gray-400">-</span>
                                          )}
                                        </td>
                                        <td className="py-2 px-2 sm:px-3 text-center">
                                          <span className="font-medium text-blue-700 text-xs sm:text-sm">
                                            {prediction.points_breakdown?.man_of_match?.points || 0}
                                          </span>
                                        </td>
                                      </tr>
                                    )}
                                    
                                    {/* Win Margin Prediction - show if league has it or user made a prediction */}
                                    {(leagueSettings?.win_margin_prediction_enabled === 'true' || (prediction.win_margin_prediction != null && prediction.win_margin_prediction !== '')) && (
                                      <tr className="border-b border-gray-100">
                                        <td className="py-2 px-2 sm:px-3">
                                          <span className="font-medium text-xs sm:text-sm">
                                            {prediction.win_margin_prediction != null && prediction.win_margin_prediction !== ''
                                              ? formatWinMarginLabel(prediction.win_margin_prediction)
                                              : 'Not predicted'}
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 sm:px-3">
                                          <span className="font-medium text-xs sm:text-sm">
                                            {prediction.win_margin_result != null && prediction.win_margin_result !== ''
                                              ? formatWinMarginLabel(prediction.win_margin_result)
                                              : 'Not available'}
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 sm:px-3 text-center">
                                          {prediction.win_margin_prediction != null && prediction.win_margin_prediction !== '' ? (
                                            prediction.points_breakdown?.win_margin?.correct ? (
                                              <span className="text-green-600 font-bold">✓</span>
                                            ) : (
                                              <span className="text-red-600 font-bold">✗</span>
                                            )
                                          ) : (
                                            <span className="text-gray-400">-</span>
                                          )}
                                        </td>
                                        <td className="py-2 px-2 sm:px-3 text-center">
                                          <span className="font-medium text-blue-700 text-xs sm:text-sm">
                                            {prediction.points_breakdown?.win_margin?.points || 0}
                                          </span>
                                        </td>
                                      </tr>
                                    )}
                                    
                                    {/* Win Streak Row */}
                                    {(() => {
                                      console.log('🔍 Win Streak Debug for prediction:', {
                                        predictionId: prediction.id,
                                        pointsBreakdown: prediction.points_breakdown,
                                        streakBonus: prediction.points_breakdown?.streak_bonus,
                                        hasBreakdown: !!prediction.points_breakdown,
                                        totalPoints: prediction.total_points,
                                        pointsAwarded: prediction.points_awarded
                                      });
                                      // Always show streak rows if we have any prediction data
                                      return true;
                                    })() && (
                                      <tr className="border-b border-gray-100">
                                        <td className="py-2 px-2 sm:px-3">
                                          <span className="font-medium text-xs sm:text-sm">Win Streak</span>
                                        </td>
                                        <td className="py-2 px-2 sm:px-3">
                                          <span className="font-medium text-xs sm:text-sm">
                                            {prediction.points_breakdown?.streak_bonus?.currentStreak || 0} consecutive correct
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 sm:px-3 text-center">
                                          {prediction.points_breakdown?.streak_bonus?.hasBonus ? (
                                            <span className="text-green-600 font-bold">✓</span>
                                          ) : (
                                            <span className="text-gray-400">-</span>
                                          )}
                                        </td>
                                        <td className="py-2 px-2 sm:px-3 text-center">
                                          <span className="font-medium text-green-700 text-xs sm:text-sm">
                                            {prediction.points_breakdown?.streak_bonus?.points || 0}
                                          </span>
                                        </td>
                                      </tr>
                                    )}
                                    
                                    {/* Wrong Streak Row */}
                                    {(() => {
                                      console.log('🔍 Wrong Streak Debug for prediction:', {
                                        predictionId: prediction.id,
                                        pointsBreakdown: prediction.points_breakdown,
                                        streakPenalty: prediction.points_breakdown?.streak_penalty,
                                        hasBreakdown: !!prediction.points_breakdown
                                      });
                                      // Always show streak rows if we have any prediction data
                                      return true;
                                    })() && (
                                      <tr className="border-b border-gray-100">
                                        <td className="py-2 px-2 sm:px-3">
                                          <span className="font-medium text-xs sm:text-sm">Wrong Streak</span>
                                        </td>
                                        <td className="py-2 px-2 sm:px-3">
                                          <span className="font-medium text-xs sm:text-sm">
                                            {prediction.points_breakdown?.streak_penalty?.currentStreak || 0} consecutive wrong
                                          </span>
                                        </td>
                                        <td className="py-2 px-2 sm:px-3 text-center">
                                          {prediction.points_breakdown?.streak_penalty?.hasPenalty ? (
                                            <span className="text-red-600 font-bold">✗</span>
                                          ) : (
                                            <span className="text-gray-400">-</span>
                                          )}
                                        </td>
                                        <td className="py-2 px-2 sm:px-3 text-center">
                                          <span className="font-medium text-red-700 text-xs sm:text-sm">
                                            {prediction.points_breakdown?.streak_penalty?.points || 0}
                                          </span>
                                        </td>
                                      </tr>
                                    )}
                                    
                                    {/* Total Points Row */}
                                    <tr className="bg-blue-50 border-t-2 border-blue-200">
                                      <td className="py-2 px-2 sm:px-3 font-semibold text-gray-700 text-xs sm:text-sm" colSpan={3}>
                                        Total Points Earned
                                      </td>
                                      <td className="py-2 px-2 sm:px-3 text-center">
                                        <span className="font-bold text-blue-800 text-sm sm:text-lg">
                                          {prediction.total_points || prediction.points_awarded || 0}
                                        </span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                          })()}
                          
                          {/* Enhanced Prediction Fields - Show immediately when match is selected */}
                          {leagueSettings && !isCompleted && (
                            <div className="space-y-4">
                                  {/* Total Runs Prediction */}
                                  {(leagueSettings.total_runs_exact_prediction_enabled === 'true' || 
                                    leagueSettings.total_runs_close_prediction_enabled === 'true' || 
                                    leagueSettings.total_runs_medium_prediction_enabled === 'true') && (
                                    <div className="bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-200">
                                      <div className="flex items-center space-x-2 sm:space-x-3">
                                        <label className="text-blue-800 font-semibold text-sm sm:text-base whitespace-nowrap flex items-center">
                                          <span className="mr-1 sm:mr-2">🏏</span>
                                          Total Runs:
                                        </label>
                                        <input
                                          type="number"
                                          min="1"
                                          max="500"
                                          placeholder="Enter runs"
                                          value={totalRunsPredictions[match.id] || ''}
                                          onChange={(e) => handleTotalRunsPrediction(match.id, parseInt(e.target.value) || 0)}
                                          className="flex-1 py-2 px-3 text-sm sm:text-base border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                      </div>
                                    </div>
                                  )}

                                  {/* Player of the Match Prediction */}
                                  {leagueSettings.man_of_match_prediction_enabled === 'true' && (
                                    <div className="bg-purple-50 rounded-lg p-3 sm:p-4 border border-purple-200">
                                      <div className="flex items-center space-x-2 sm:space-x-3">
                                        <label className="text-purple-800 font-semibold text-sm sm:text-base whitespace-nowrap flex items-center flex-shrink-0">
                                          <span className="mr-1 sm:mr-2">🏆</span>
                                          POM:
                                        </label>
                                        <select
                                      key={`mom-dropdown-${match.id}`}
                                      value={momPredictions[match.id]?.id ? momPredictions[match.id].id.toString() : ''}
                                          onChange={(e) => {
                                        console.log('🔍 MoM dropdown onChange - Raw event:', e.target.value, e.target.options[e.target.selectedIndex]?.text);
                                        console.log('🔍 All options:', Array.from(e.target.options as any).map((opt: any) => ({ value: opt.value, text: opt.text })));
                                            const selectedOption = e.target.options[e.target.selectedIndex];
                                        console.log('🔍 Selected option:', selectedOption);
                                        
                                        if (selectedOption && selectedOption.value) {
                                          const playerId = selectedOption.value; // Keep as string for UUIDs
                                          const squad = matchSquadPlayers[match.id];
                                          const player = squad?.find((p: any) => p.id?.toString() === playerId);
                                          const playerName = player?.name ?? selectedOption.text; // Send name only so it matches admin result format
                                          console.log('🔍 Parsed values:', { playerId, playerName, type: typeof playerId });
                                          handleMomPrediction(match.id, playerId, playerName);
                                        } else {
                                          console.log('🔍 Clearing MoM prediction for match:', match.id);
                                          // Clear the prediction if empty option is selected
                                          setMomPredictions(prev => {
                                            const newPredictions = { ...prev };
                                            delete newPredictions[match.id];
                                            return newPredictions;
                                          });
                                            }
                                          }}
                                          onFocus={() => {
                                        ensureSquadDataLoaded(match.id);
                                          }}
                                          className="flex-1 min-w-0 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        >
                                          <option value="">Select player...</option>
                                      {!matchSquadPlayers[match.id] ? (
                                        <option value="" disabled>Loading players...</option>
                                      ) : matchSquadPlayers[match.id].length === 0 ? (
                                        <option value="" disabled>No players available</option>
                                      ) : (
                                        matchSquadPlayers[match.id].map((player: any) => (
                                          <option key={player.id} value={player.id.toString()}>
                                            {player.name} ({player.team})
                                            </option>
                                        ))
                                      )}
                                        </select>

                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Win Margin Prediction */}
                                  {leagueSettings?.win_margin_prediction_enabled === 'true' && (
                                    <div className="mt-3">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <label className="text-xs sm:text-sm font-medium text-teal-700 flex items-center whitespace-nowrap">
                                          <span className="mr-1 sm:mr-2">🎯</span>
                                          Win Margin:
                                        </label>
                                        <select
                                          value={winMarginPredictions[match.id] || ''}
                                          onChange={(e) => {
                                            if (e.target.value) {
                                              setWinMarginPredictions(prev => ({
                                                ...prev,
                                                [match.id]: e.target.value
                                              }));
                                            } else {
                                              setWinMarginPredictions(prev => {
                                                const newPredictions = { ...prev };
                                                delete newPredictions[match.id];
                                                return newPredictions;
                                              });
                                            }
                                          }}
                                          className="flex-1 min-w-0 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-teal-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        >
                                          <option value="">Select margin...</option>
                                          <option value="hard">Hard (&lt; 10 runs/balls)</option>
                                          <option value="medium">Medium (10-24)</option>
                                          <option value="easy">Easy (25+)</option>
                                        </select>
                                      </div>
                                    </div>
                                  )}
                            </div>
                              )}

                          {/* Save Prediction Button - Show when there are any predictions */}
                          {(currentPrediction || totalRunsPredictions[match.id] || momPredictions[match.id] || winMarginPredictions[match.id]) && !isCompleted && (
                              <div className="text-center">
                                <button
                                onClick={async () => {
                                  try {
                                    const leagueId = parseInt(id!);
                                    
                                    // Get enhanced prediction data
                                    const totalRuns = totalRunsPredictions[match.id] || undefined;
                                    const momPrediction = momPredictions[match.id];
                                    const momPlayerId = momPrediction?.id || undefined;
                                    const momPlayerName = momPrediction?.name || undefined;
                                    const winMarginPrediction = winMarginPredictions[match.id] || undefined;
                                    
                                    console.log('🔍 Saving dropdown prediction for match:', match.id, {
                                      predictedWinner: currentPrediction,
                                      totalRuns,
                                      momPlayerId,
                                      momPlayerName,
                                      winMarginPrediction
                                    });
                                    
                                    const saveResDropdown: any = await apiService.makePickemPrediction(
                                      leagueId,
                                      match.id,
                                      currentPrediction,
                                      totalRuns,
                                      momPlayerId,
                                      momPlayerName,
                                      winMarginPrediction
                                    );

                                    void requestPickConfirmationEmail(saveResDropdown?.transactionId);

                                    // Reload predictions
                                    const predictionsData = await apiService.getUserPickemPredictions(leagueId);
                                    setPredictions(predictionsData.predictions);
                                    
                                    await refreshPickemStandings();
                                    
                                    // Update batch state to reflect saved data
                                    const updatedBatchPredictions = { ...batchPredictions };
                                    const updatedTotalRuns = { ...totalRunsPredictions };
                                    const updatedMomPredictions = { ...momPredictions };
                                    const updatedWinMarginPredictions = { ...winMarginPredictions };
                                    
                                    // Keep the saved predictions in batch state
                                    if (currentPrediction) {
                                      updatedBatchPredictions[match.id] = currentPrediction;
                                    }
                                    if (totalRuns) {
                                      updatedTotalRuns[match.id] = totalRuns;
                                    }
                                    if (momPlayerId && momPlayerName) {
                                      updatedMomPredictions[match.id] = {
                                        id: momPlayerId,
                                        name: momPlayerName
                                      };
                                    }
                                    if (winMarginPrediction) {
                                      updatedWinMarginPredictions[match.id] = winMarginPrediction;
                                    }
                                    
                                    setBatchPredictions(updatedBatchPredictions);
                                    setTotalRunsPredictions(updatedTotalRuns);
                                    setMomPredictions(updatedMomPredictions);
                                    setWinMarginPredictions(updatedWinMarginPredictions);
                                    
                                    console.log('🔍 Dropdown prediction saved successfully');
                                    if (showLeagueActivity) {
                                      loadUserActivity();
                                    }
                                    setNotification('Prediction saved successfully!');
                                    setTimeout(() => setNotification(null), 3000);
                                  } catch (err: any) {
                                    console.error('🔍 Error saving dropdown prediction:', err);
                                    setError(err.message || 'Failed to save prediction');
                                  }
                                }}
                                  className="bg-green-600 text-white py-2.5 sm:py-3 px-6 sm:px-8 rounded-lg text-base sm:text-lg font-medium hover:bg-green-700 transition-all duration-200"
                                >
                                  Save
                                </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Batch Predictions View */}
                  <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-center mb-4 sm:mb-6">
                      <button
                        onClick={handleSaveBatchPredictions}
                        disabled={(Object.keys(batchPredictions).length === 0 && Object.keys(totalRunsPredictions).length === 0 && Object.keys(momPredictions).length === 0 && Object.keys(winMarginPredictions).length === 0) || savingPredictions}
                        className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 ${
                          (Object.keys(batchPredictions).length > 0 || Object.keys(totalRunsPredictions).length > 0 || Object.keys(momPredictions).length > 0 || Object.keys(winMarginPredictions).length > 0) && !savingPredictions
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {savingPredictions ? 'Saving...' : 'Save'}
                      </button>
                    </div>

                    {/* Matches by Date */}
                    {Object.entries(getMatchesByDate()).map(([dateKey, dateMatches]) => (
                      <div key={dateKey} className="mb-8">
                        <h4 className="text-primary font-semibold mb-4 border-b border-gray-200 pb-2">
                          📅 {formatGroupDateLabel(dateKey)}
                        </h4>
                        
                        <div className="space-y-4">
                          {dateMatches.map((match, index) => {
                            const prediction = getPredictionForMatch(match.id);
                            const currentPrediction = getCurrentPrediction(match.id);
                            

                            
                            return (
                              <div key={match.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                <div className="flex items-center justify-between mb-4">
                                  <h5 className="text-primary font-medium">
                                    {match.name}
                                  </h5>
                                  <span className="text-sm text-gray-600">
                                    {formatDate(match.match_date)}
                                  </span>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                                  <button
                                    onClick={() => handleBatchPrediction(match.id, match.team1_name)}
                                    className={`flex items-center justify-center space-x-1 sm:space-x-2 py-2 sm:py-3 px-2 sm:px-4 rounded-lg border transition-all duration-300 hover:bg-gray-50 relative ${
                                      currentPrediction === match.team1_name
                                        ? 'border-yellow-500 bg-yellow-200 shadow-lg transform scale-105'
                                        : 'border-gray-300 bg-white'
                                    }`}
                                  >
                                    <span className={`text-xs sm:text-sm md:text-base font-semibold ${
                                      currentPrediction === match.team1_name 
                                        ? 'text-yellow-800'
                                        : 'text-primary'
                                    }`}>{match.team1_name}</span>
                                    {(getSavedPrediction(match.id) === match.team1_name || currentPrediction === match.team1_name) && (
                                      <span className="text-green-600">
                                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </span>
                                    )}

                                  </button>
                                  <button
                                    onClick={() => handleBatchPrediction(match.id, match.team2_name)}
                                    className={`flex items-center justify-center space-x-1 sm:space-x-2 py-2 sm:py-3 px-2 sm:px-4 rounded-lg border transition-all duration-300 hover:bg-gray-50 relative ${
                                      currentPrediction === match.team2_name
                                        ? 'border-yellow-500 bg-yellow-200 shadow-lg transform scale-105'
                                        : 'border-gray-300 bg-white'
                                    }`}
                                  >
                                    <span className={`text-xs sm:text-sm md:text-base font-semibold ${
                                      currentPrediction === match.team2_name 
                                        ? 'text-yellow-800'
                                        : 'text-primary'
                                    }`}>{match.team2_name}</span>
                                    {(getSavedPrediction(match.id) === match.team2_name || currentPrediction === match.team2_name) && (
                                      <span className="text-green-600">
                                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </span>
                                    )}

                                  </button>
                                </div>

                                {(() => {
                                  const st = getMatchStatusLocal(match);
                                  const done = st === 'Completed' || isMatchLockedLocal(match);
                                  if (done || !league?.dynamic_scoring_enabled || !id) return null;
                                  return (
                                    <DynamicPickScoringNote leagueId={parseInt(id, 10)} matchId={String(match.id)} />
                                  );
                                })()}

                                {/* Enhanced Prediction Fields for Batch View */}
                                {currentPrediction && leagueSettings && (
                                  <div className="mt-3 sm:mt-4 space-y-2 sm:space-y-3">
                                    {/* Total Runs Prediction */}
                                    {(leagueSettings.total_runs_exact_prediction_enabled === 'true' || 
                                      leagueSettings.total_runs_close_prediction_enabled === 'true' || 
                                      leagueSettings.total_runs_medium_prediction_enabled === 'true') && (
                                      <div className="flex items-center space-x-2 sm:space-x-3">
                                        <label className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap flex items-center flex-shrink-0">
                                          <span className="mr-1">🏏</span>
                                          Total Runs:
                                        </label>
                                        <input
                                          type="number"
                                          min="1"
                                          max="500"
                                          placeholder="Enter runs"
                                          value={totalRunsPredictions[match.id] || ''}
                                          onChange={(e) => handleTotalRunsPrediction(match.id, parseInt(e.target.value) || 0)}
                                          className="flex-1 min-w-0 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />

                                      </div>
                                    )}

                                    {/* Player of the Match Prediction */}
                                    {leagueSettings.man_of_match_prediction_enabled === 'true' && (
                                      <div className="flex items-center space-x-2 sm:space-x-3">
                                        <label className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap flex items-center flex-shrink-0">
                                          <span className="mr-1">🏆</span>
                                          POM:
                                        </label>
                                        <select
                                          key={`batch-mom-dropdown-${match.id}`}
                                          value={momPredictions[match.id]?.id ? momPredictions[match.id].id.toString() : ''}
                                          onChange={(e) => {
                                            console.log('🔍 Batch MoM dropdown onChange - Raw event:', e.target.value, e.target.options[e.target.selectedIndex]?.text);
                                            console.log('🔍 All batch options:', Array.from(e.target.options as any).map((opt: any) => ({ value: opt.value, text: opt.text })));
                                            const selectedOption = e.target.options[e.target.selectedIndex];
                                            console.log('🔍 Selected batch option:', selectedOption);
                                            
                                            if (selectedOption && selectedOption.value) {
                                              const playerId = selectedOption.value; // Keep as string for UUIDs
                                              const squad = matchSquadPlayers[match.id];
                                              const player = squad?.find((p: any) => p.id?.toString() === playerId);
                                              const playerName = player?.name ?? selectedOption.text; // Send name only so it matches admin result format
                                              console.log('🔍 Parsed batch values:', { playerId, playerName, type: typeof playerId });
                                              handleMomPrediction(match.id, playerId, playerName);
                                            } else {
                                              console.log('🔍 Clearing Batch MoM prediction for match:', match.id);
                                              // Clear the prediction if empty option is selected
                                              setMomPredictions(prev => {
                                                const newPredictions = { ...prev };
                                                delete newPredictions[match.id];
                                                return newPredictions;
                                              });
                                            }
                                          }}
                                          onFocus={() => {
                                            ensureSquadDataLoaded(match.id);
                                          }}
                                          className="flex-1 min-w-0 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        >
                                          <option value="">Select player...</option>
                                          {!matchSquadPlayers[match.id] ? (
                                            <option value="" disabled>Loading players...</option>
                                          ) : matchSquadPlayers[match.id].length === 0 ? (
                                            <option value="" disabled>No players available</option>
                                          ) : (
                                            matchSquadPlayers[match.id].map((player: any) => (
                                              <option key={player.id} value={player.id.toString()}>
                                                {player.name} ({player.team})
                                            </option>
                                            ))
                                          )}
                                        </select>
                                       
                                      </div>
                                    )}
                                    
                                    {/* Win Margin Prediction */}
                                    {leagueSettings.win_margin_prediction_enabled === 'true' && (
                                      <div className="flex items-center space-x-2 sm:space-x-3">
                                        <label className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap flex items-center flex-shrink-0">
                                          <span className="mr-1">🎯</span>
                                          Win Margin:
                                        </label>
                                        <select
                                          value={winMarginPredictions[match.id] || ''}
                                          onChange={(e) => {
                                            if (e.target.value) {
                                              setWinMarginPredictions(prev => ({
                                                ...prev,
                                                [match.id]: e.target.value
                                              }));
                                            } else {
                                              setWinMarginPredictions(prev => {
                                                const newPredictions = { ...prev };
                                                delete newPredictions[match.id];
                                                return newPredictions;
                                              });
                                            }
                                          }}
                                          className="flex-1 min-w-0 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                                        >
                                          <option value="">Select margin...</option>
                                          <option value="hard">Hard (&lt; 10 runs/balls)</option>
                                          <option value="medium">Medium (10-24)</option>
                                          <option value="easy">Easy (25+)</option>
                                        </select>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {Object.keys(getMatchesByDate()).length === 0 && (
                      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-800 text-sm">
                        No fixtures are currently open for picks in this section.
                      </div>
                    )}
                  </div>

                  {/* Past matches: locked, started, completed, or abandoned (view only) */}
                  {Object.keys(getNonPickableMatchesByDate()).length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mt-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-gray-600 flex items-center">
                          <span className="mr-2">📋</span>
                          Past matches
                        </h3>
                        <span className="text-sm text-gray-500">
                          {matches.filter((m) => !matchStillAcceptsPicks(m)).length} match
                          {matches.filter((m) => !matchStillAcceptsPicks(m)).length !== 1 ? 'es' : ''} — view only
                        </span>
                </div>

                      <div className="space-y-6">
                        {Object.entries(getNonPickableMatchesByDate()).map(([dateKey, dateMatches]) => (
                          <div key={dateKey}>
                            <h4 className="text-primary font-semibold mb-4 border-b border-gray-200 pb-2">
                              📅{' '}
                              {formatGroupDateLabel(dateKey)}
                            </h4>
                            <div className="space-y-4">
                        {dateMatches.map((match) => {
                          const prediction = getPredictionForMatch(match.id);
                          const savedPrediction = getSavedPrediction(match.id);
                          
                          return (
                            <div key={match.id} className="bg-gray-100 rounded-lg p-4 border border-gray-300 opacity-75">
                              <div className="flex items-center justify-between mb-3">
                                <h5 className="text-gray-700 font-medium">
                                  {match.name}
                                </h5>
                <div className="flex items-center space-x-2">
                                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                                    {getMatchStatusLocal(match) === 'Completed'
                                      ? '✓ Completed'
                                      : getMatchStatusLocal(match) === 'Abandoned'
                                        ? 'Abandoned'
                                        : '🔒 Locked'}
                                  </span>
                                  <span className="text-xs text-gray-600">
                                    {formatDate(match.match_date)}
                                  </span>
                </div>
              </div>

                              <div className="grid md:grid-cols-2 gap-4 mb-3">
                                <div className={`text-center py-2 px-3 rounded-lg border ${
                                  savedPrediction === match.team1_name
                                    ? 'border-green-500 bg-green-50 text-green-800'
                                    : 'border-gray-300 bg-gray-50 text-gray-600'
                                }`}>
                                  <span className="font-medium">{match.team1_name}</span>
                                  {savedPrediction === match.team1_name && (
                                    <span className="ml-2 text-green-600">✓</span>
                          )}
                        </div>
                                <div className={`text-center py-2 px-3 rounded-lg border ${
                                  savedPrediction === match.team2_name
                                    ? 'border-green-500 bg-green-50 text-green-800'
                                    : 'border-gray-300 bg-gray-50 text-gray-600'
                                }`}>
                                  <span className="font-medium">{match.team2_name}</span>
                                  {savedPrediction === match.team2_name && (
                                    <span className="ml-2 text-green-600">✓</span>
                          )}
                        </div>
                      </div>
                      
                              {/* Show saved enhanced predictions */}
                              {savedPrediction && !league?.dynamic_scoring_enabled && (
                                <div className="text-sm text-gray-600 space-y-1">
                                  {prediction?.total_runs_prediction && (
                                    <p>🏏 Total Runs: <span className="font-medium">{prediction.total_runs_prediction}</span></p>
                                  )}
                                  {prediction?.mom_player_name && (
                                    <p>🏆 MoM: <span className="font-medium">{prediction.mom_player_name}</span></p>
                                  )}
                        </div>
                              )}

                              {/* Match Result for Completed Matches */}
                              {(() => {
                                console.log('🔍 Locked match debug:', {
                                  matchId: match.id,
                                  matchStatus: match.status,
                                  hasPrediction: !!prediction,
                                  prediction: prediction,
                                  pointsBreakdown: prediction?.points_breakdown,
                                  hasWinner: !!match.winner,
                                  hasPointsBreakdown: !!prediction?.points_breakdown
                                });
                                // Show match results if match has a winner OR if prediction has points breakdown (indicating completed match)
                                return (match.winner || prediction?.points_breakdown) && prediction;
                              })() && prediction && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <h5 className="text-gray-800 font-semibold mb-3 flex items-center">
                                      <span className="mr-2">📊</span>
                                      Match Results Summary
                                    </h5>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="border-b border-gray-200">
                                            <th className="text-left py-2 px-3 font-semibold text-gray-700">Your Predictions</th>
                                            <th className="text-left py-2 px-3 font-semibold text-gray-700">Match Results</th>
                                            <th className="text-center py-2 px-3 font-semibold text-gray-700">Correct?</th>
                                            <th className="text-center py-2 px-3 font-semibold text-gray-700">Points</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {/* Team Winner Prediction */}
                                          <tr className="border-b border-gray-100">
                                                                                         <td className="py-2 px-2 sm:px-3">
                                               <span className="font-medium text-xs sm:text-sm">{prediction?.predicted_winner}</span>
                                             </td>
                                             <td className="py-2 px-2 sm:px-3">
                                               <span className="font-medium text-xs sm:text-sm">{prediction?.winner || 'Not available'}</span>
                                             </td>
                                             <td className="py-2 px-2 sm:px-3 text-center">
                                               {prediction?.is_correct ? (
                                                 <span className="text-green-600 font-bold">✓</span>
                                               ) : (
                                                 <span className="text-red-600 font-bold">✗</span>
                                               )}
                                             </td>
                                             <td className="py-2 px-2 sm:px-3 text-center">
                                               <span className="font-medium text-blue-700 text-xs sm:text-sm">
                                                 {prediction?.points_breakdown?.match_winner?.points || 0}
                                               </span>
                                             </td>
                                          </tr>
                                          
                                          {/* Total Runs Prediction */}
                                          {!league?.dynamic_scoring_enabled &&
                                            (leagueSettings?.total_runs_exact_prediction_enabled === 'true' || 
                                            leagueSettings?.total_runs_close_prediction_enabled === 'true' || 
                                            leagueSettings?.total_runs_medium_prediction_enabled === 'true') && (
                                            <tr className="border-b border-gray-100">
                                              <td className="py-2 px-2 sm:px-3">
                                                <span className="font-medium text-xs sm:text-sm">
                                                  {prediction?.total_runs_prediction ? `${prediction.total_runs_prediction} runs` : 'Not predicted'}
                                        </span>
                                              </td>
                                              <td className="py-2 px-2 sm:px-3">
                                                <span className="font-medium text-xs sm:text-sm">
                                                  {prediction?.total_runs_result || prediction?.tournament_total_runs || 'Not available'}
                                        </span>
                                              </td>
                                              <td className="py-2 px-2 sm:px-3 text-center">
                                                {prediction?.total_runs_prediction ? (
                                                  prediction?.points_breakdown?.total_runs?.correct ? (
                                                    <span className="text-green-600 font-bold">✓</span>
                                                  ) : (
                                                    <span className="text-red-600 font-bold">✗</span>
                                                  )
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                )}
                                              </td>
                                              <td className="py-2 px-2 sm:px-3 text-center">
                                                <span className="font-medium text-blue-700 text-xs sm:text-sm">
                                                  {prediction?.points_breakdown?.total_runs?.points || 0}
                                                </span>
                                              </td>
                                            </tr>
                                          )}
                                          
                                          {/* Man of Match Prediction */}
                                          {!league?.dynamic_scoring_enabled &&
                                            leagueSettings?.man_of_match_prediction_enabled === 'true' && (
                                            <tr className="border-b border-gray-100">
                                              <td className="py-2 px-2 sm:px-3">
                                                <span className="font-medium text-xs sm:text-sm">
                                                  {prediction?.mom_player_name || 'Not predicted'}
                                                </span>
                                              </td>
                                              <td className="py-2 px-2 sm:px-3">
                                                <span className="font-medium text-xs sm:text-sm">
                                                  {prediction?.mom_result || prediction?.tournament_man_of_match || 'Not available'}
                                                </span>
                                              </td>
                                              <td className="py-2 px-2 sm:px-3 text-center">
                                                {prediction?.mom_player_name ? (
                                                  prediction?.points_breakdown?.man_of_match?.correct ? (
                                                    <span className="text-green-600 font-bold">✓</span>
                                                  ) : (
                                                    <span className="text-red-600 font-bold">✗</span>
                                                  )
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                )}
                                              </td>
                                              <td className="py-2 px-2 sm:px-3 text-center">
                                                <span className="font-medium text-blue-700 text-xs sm:text-sm">
                                                  {prediction?.points_breakdown?.man_of_match?.points || 0}
                                                </span>
                                              </td>
                                            </tr>
                                          )}
                                          
                                          {/* Win Margin Prediction */}
                                          {leagueSettings?.win_margin_prediction_enabled === 'true' && (
                                            <tr className="border-b border-gray-100">
                                              <td className="py-2 px-2 sm:px-3">
                                                <span className="font-medium text-xs sm:text-sm">
                                                  {prediction?.win_margin_prediction ? formatWinMarginLabel(prediction.win_margin_prediction) : 'Not predicted'}
                                                </span>
                                              </td>
                                              <td className="py-2 px-2 sm:px-3">
                                                <span className="font-medium text-xs sm:text-sm">
                                                  {prediction?.win_margin_result != null && prediction.win_margin_result !== '' ? formatWinMarginLabel(prediction.win_margin_result) : 'Not available'}
                                                </span>
                                              </td>
                                              <td className="py-2 px-2 sm:px-3 text-center">
                                                {prediction?.win_margin_prediction ? (
                                                  prediction?.points_breakdown?.win_margin?.correct ? (
                                                    <span className="text-green-600 font-bold">✓</span>
                                                  ) : (
                                                    <span className="text-red-600 font-bold">✗</span>
                                                  )
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                )}
                                              </td>
                                              <td className="py-2 px-2 sm:px-3 text-center">
                                                <span className="font-medium text-blue-700 text-xs sm:text-sm">
                                                  {prediction?.points_breakdown?.win_margin?.points || 0}
                                                </span>
                                              </td>
                                            </tr>
                                          )}
                                          
                                          {/* Win Streak Row — classic pick'em only; dynamic scoring omits streak breakdown */}
                                          {!league?.dynamic_scoring_enabled && (
                                            <tr className="border-b border-gray-100">
                                              <td className="py-2 px-3">
                                                <span className="font-medium">Win Streak</span>
                                              </td>
                                              <td className="py-2 px-3">
                                                <span className="font-medium">
                                                  {prediction?.points_breakdown?.streak_bonus?.currentStreak ?? 0} consecutive correct
                                        </span>
                                              </td>
                                              <td className="py-2 px-3 text-center">
                                                {prediction?.points_breakdown?.streak_bonus?.hasBonus ? (
                                                  <span className="text-green-600 font-bold">✓</span>
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                )}
                                              </td>
                                              <td className="py-2 px-3 text-center">
                                                <span className="font-medium text-green-700">
                                                  {prediction?.points_breakdown?.streak_bonus?.points ?? 0}
                                                </span>
                                              </td>
                                            </tr>
                                          )}
                                          
                                          {/* Wrong Streak Row */}
                                          {!league?.dynamic_scoring_enabled && (
                                            <tr className="border-b border-gray-100">
                                              <td className="py-2 px-3">
                                                <span className="font-medium">Wrong Streak</span>
                                              </td>
                                              <td className="py-2 px-3">
                                                <span className="font-medium">
                                                  {prediction?.points_breakdown?.streak_penalty?.currentStreak ?? 0} consecutive wrong
                                                </span>
                                              </td>
                                              <td className="py-2 px-3 text-center">
                                                {prediction?.points_breakdown?.streak_penalty?.hasPenalty ? (
                                                  <span className="text-red-600 font-bold">✗</span>
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                )}
                                              </td>
                                              <td className="py-2 px-3 text-center">
                                                <span className="font-medium text-red-700">
                                                  {prediction?.points_breakdown?.streak_penalty?.points ?? 0}
                                                </span>
                                              </td>
                                            </tr>
                                          )}
                                          
                                          {/* Total Points Row */}
                                          <tr className="bg-blue-50 border-t-2 border-blue-200">
                                            <td className="py-2 px-3 font-semibold text-gray-700" colSpan={3}>
                                              Total Points Earned
                                            </td>
                                            <td className="py-2 px-3 text-center">
                                              <span className="font-bold text-blue-800 text-lg">
                                                {prediction?.total_points || prediction?.points_awarded || 0}
                                              </span>
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                    </div>
                                  </div>
                                )}

                              {!savedPrediction && (
                                <p className="text-sm text-red-600 font-medium">
                                  ⚠️ No prediction made for this match
                                </p>
                              )}
                              </div>
                          );
                        })}
                            </div>
                          </div>
                        ))}
                          </div>
                        </div>
                  )}
                  {Object.keys(getMatchesByDate()).length === 0 &&
                    Object.keys(getNonPickableMatchesByDate()).length === 0 && (
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mt-6 text-gray-600 text-sm">
                        Matches are not available yet for this league.
                      </div>
                    )}

                        </div>
                      )}
                    </div>
          )}

          {activeTab === 'all-picks' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <span className="mr-3">👥</span>
                  All Participants' Picks
                </h2>
              </div>
              {/* Only All Matches View below, no toggle buttons, no Current Match view */}
                <div className="space-y-6">
                  {/* Match Selection Dropdown */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <h3 className="text-lg font-bold text-primary mb-4">Select Match</h3>
                    <select
                      value={selectedAllPicksMatch || ''}
                      onChange={(e) => setSelectedAllPicksMatch(e.target.value || null)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                    >
                      <option value="">Choose a match...</option>
                      {matches
                        .sort((a, b) => new Date(a.match_date).getTime() - new Date(b.match_date).getTime())
                        .map((match, index) => (
                          <option key={match.id} value={match.id}>
                            {match.name} - {formatDate(match.match_date)} ({getMatchStatusLocal(match)})
                          </option>
                        ))}
                    </select>
                  </div>
                  {allPicksLoading && (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-gray-600">
                      Loading all picks...
                    </div>
                  )}
                  {/* Selected Match Picks */}
                  {!allPicksLoading && selectedAllPicksMatch && (() => {
                    const match = matches.find(m => m.id === selectedAllPicksMatch);
                    if (!match) return null;
                  const hasStarted = isMatchLockedLocal(match);
                  const showAllPicksTable =
                    hasStarted || !!league?.pickem_all_picks_always_visible;
                  const matchStatus = getMatchStatusLocal(match);
                  const isCompleted = matchStatus === 'Completed';
                  const isAbandoned = matchStatus === 'Abandoned';
                  /** Show Points column once scoring has been applied (completed or abandoned/NR). */
                  const showPointsColumn = isCompleted || isAbandoned;
                  // Filter predictions for this specific match from all predictions
                  const matchPredictions = allPredictions.filter(p => p.match_id === match.id);
                    return (
                      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg sm:text-xl font-bold text-primary mb-1 flex items-center gap-2">
                              {match.name}
                              {isCompleted && (
                                <span className="text-green-600 text-lg sm:text-xl">✓</span>
                              )}
                            </h3>
                            <div className="text-gray-600 text-xs sm:text-sm flex flex-wrap items-center gap-2">
                              <span>📅 {formatDate(match.match_date)}</span>
                              <span className="hidden sm:inline">•</span>
                              <span>🏟️ {pickemVenueDisplayLabel(match.venue)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Team Selection Display */}
                        <div className="space-y-6 mb-6">
                          <h4 className="text-primary font-semibold text-center text-sm sm:text-base">
                            {isCompleted ? 'Match Result:' : 'Teams:'}
                          </h4>
                          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                            <div className={`flex items-center justify-center space-x-2 sm:space-x-3 py-2 sm:py-3 md:py-4 px-3 sm:px-4 md:px-6 rounded-lg border transition-all duration-300 ${
                              isCompleted
                                ? match.winner === match.team1_name
                                  ? 'border-green-500 bg-green-100 shadow-lg'
                                  : 'border-gray-300 bg-gray-50'
                                : 'border-gray-300 bg-white'
                            }`}>
                              <span className={`text-sm sm:text-base md:text-lg font-semibold ${
                                isCompleted
                                  ? match.winner === match.team1_name
                                    ? 'text-green-800'
                                    : 'text-gray-600'
                                  : 'text-primary'
                              }`}>{match.team1_name}</span>
                              {isCompleted && match.winner === match.team1_name && (
                                <span className="text-green-600 font-bold text-sm sm:text-base">🏆</span>
                              )}
                            </div>
                            <div className={`flex items-center justify-center space-x-2 sm:space-x-3 py-2 sm:py-3 md:py-4 px-3 sm:px-4 md:px-6 rounded-lg border transition-all duration-300 ${
                              isCompleted
                                ? match.winner === match.team2_name
                                  ? 'border-green-500 bg-green-100 shadow-lg'
                                  : 'border-gray-300 bg-gray-50'
                                : 'border-gray-300 bg-white'
                            }`}>
                              <span className={`text-sm sm:text-base md:text-lg font-semibold ${
                                isCompleted
                                  ? match.winner === match.team2_name
                                    ? 'text-green-800'
                                    : 'text-gray-600'
                                  : 'text-primary'
                              }`}>{match.team2_name}</span>
                              {isCompleted && match.winner === match.team2_name && (
                                <span className="text-green-600 font-bold text-sm sm:text-base">🏆</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {league?.dynamic_scoring_enabled && showAllPicksTable && id && (
                          <div className="mb-6">
                            <DynamicPickScoringNote
                              leagueId={parseInt(id, 10)}
                              matchId={String(match.id)}
                              persistSnapshot
                            />
                          </div>
                        )}
                        
                        {/* Match Result Summary for Completed Matches */}
                        {isCompleted && (() => {
                          // Get the first prediction to extract match results
                          const firstPrediction = matchPredictions[0];
                          const totalRunsResult = firstPrediction?.total_runs_result || firstPrediction?.tournament_total_runs;
                          const momResult = firstPrediction?.mom_result || firstPrediction?.tournament_man_of_match;
                          const winMarginResult = firstPrediction?.win_margin_result;
                          return (
                            <div className="bg-green-50 rounded-lg p-3 sm:p-4 border border-green-200 mb-4 sm:mb-6">
                              <h4 className="text-green-800 font-semibold mb-2 sm:mb-3 flex items-center text-sm sm:text-base">
                                <span className="mr-1 sm:mr-2">🏆</span>
                                <span className="text-xs sm:text-sm md:text-base">Match Results</span>
                              </h4>
                              <div className="flex flex-row items-center gap-3 sm:gap-4 flex-wrap">
                                {totalRunsResult && (
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs sm:text-sm text-green-600 font-medium whitespace-nowrap">Total Runs:</span>
                                    <span className="text-sm sm:text-base font-bold text-green-800">{totalRunsResult}</span>
                                  </div>
                                )}
                                {momResult && (
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs sm:text-sm text-green-600 font-medium whitespace-nowrap">POM:</span>
                                    <span className="text-sm sm:text-base font-bold text-green-800">{momResult}</span>
                                  </div>
                                )}
                                {leagueSettings?.win_margin_prediction_enabled === 'true' && (winMarginResult != null && winMarginResult !== '') && (
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs sm:text-sm text-green-600 font-medium whitespace-nowrap">Win Margin:</span>
                                    <span className="text-sm sm:text-base font-bold text-green-800">{formatWinMarginLabel(winMarginResult)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                        
                        {showAllPicksTable ? (
                          <div className="space-y-4">
                            <h4 className="text-primary font-semibold">All Picks:</h4>
                          {/* Tabular Display */}
                          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto -mx-2 sm:mx-0">
                              <table className="w-full text-xs sm:text-sm min-w-[500px]">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                  <tr>
                                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-primary font-semibold whitespace-nowrap">Player</th>
                                    <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-primary font-semibold whitespace-nowrap">Pick</th>
                                    <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-primary font-semibold whitespace-nowrap">Total Runs</th>
                                    {leagueSettings?.win_margin_prediction_enabled === 'true' && (
                                      <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-primary font-semibold whitespace-nowrap">Win Margin</th>
                                    )}
                                    <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-primary font-semibold whitespace-nowrap">POM</th>
                                    {showPointsColumn && (
                                    <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-primary font-semibold whitespace-nowrap">Points</th>
                                  )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {participants.map((participant) => {
                                    const prediction = matchPredictions.find(p => p.user_id === participant.user_id);
                                    return (
                                      <tr key={participant.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                                          <span className="font-medium text-primary text-xs sm:text-sm">{participant.display_name}</span>
                                          {participant.is_manager && (
                                            <span className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-accent text-primary text-xs font-medium rounded-full">
                                              Manager
                                        </span>
                                      )}
                                      </td>
                                      <td className="text-center py-3 px-2 sm:px-4">
                                        {prediction?.predicted_winner ? (
                                          <div className="flex items-center justify-center space-x-1">
                                            <span className={`text-xs sm:text-sm font-semibold ${
                                              isCompleted
                                                ? prediction.is_correct
                                                  ? 'text-green-800'
                                                  : 'text-red-800'
                                                : 'text-primary'
                                            }`}>{prediction.predicted_winner}</span>
                                            {isCompleted && (
                                              prediction.is_correct ? (
                                                <span className="text-green-600 text-sm sm:text-base">✓</span>
                                              ) : (
                                                <span className="text-red-600 text-sm sm:text-base">✗</span>
                                              )
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-gray-400 font-medium text-xs sm:text-sm">-</span>
                                        )}
                                      </td>
                                      <td className="text-center py-2 sm:py-3 px-2 sm:px-4">
                                        {prediction?.total_runs_prediction ? (
                                          <div className="flex items-center justify-center space-x-1">
                                            <span className={`text-xs sm:text-sm font-semibold ${
                                              isCompleted && prediction.points_breakdown?.total_runs
                                                ? prediction.points_breakdown.total_runs.correct
                                                  ? 'text-green-800'
                                                  : 'text-red-800'
                                                : 'text-blue-600'
                                            }`}>{prediction.total_runs_prediction}</span>
                                            {isCompleted && prediction.points_breakdown?.total_runs && (
                                              prediction.points_breakdown.total_runs.correct ? (
                                                <span className="text-green-600 text-xs sm:text-sm">✓</span>
                                              ) : (
                                                <span className="text-red-600 text-xs sm:text-sm">✗</span>
                                              )
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-gray-400 font-medium text-xs sm:text-sm">-</span>
                                        )}
                                      </td>
                                      {leagueSettings?.win_margin_prediction_enabled === 'true' && (
                                        <td className="text-center py-2 sm:py-3 px-2 sm:px-4">
                                          {prediction?.win_margin_prediction ? (
                                            <div className="flex items-center justify-center space-x-1">
                                              <span className={`text-xs sm:text-sm font-semibold ${
                                                isCompleted && prediction.points_breakdown?.win_margin
                                                  ? prediction.points_breakdown.win_margin.correct
                                                    ? 'text-green-800'
                                                    : 'text-red-800'
                                                  : 'text-blue-600'
                                              }`}>{formatWinMarginLabel(prediction.win_margin_prediction)}</span>
                                              {isCompleted && prediction.points_breakdown?.win_margin && (
                                                prediction.points_breakdown.win_margin.correct ? (
                                                  <span className="text-green-600 text-xs sm:text-sm">✓</span>
                                                ) : (
                                                  <span className="text-red-600 text-xs sm:text-sm">✗</span>
                                                )
                                              )}
                                            </div>
                                          ) : (
                                            <span className="text-gray-400 font-medium text-xs sm:text-sm">-</span>
                                          )}
                                        </td>
                                      )}
                                      <td className="text-center py-2 sm:py-3 px-2 sm:px-4">
                                        {prediction?.mom_prediction ? (
                                          <div className="flex items-center justify-center space-x-1">
                                            <span className={`text-xs sm:text-sm font-semibold ${
                                              isCompleted && prediction.points_breakdown?.man_of_match
                                                ? prediction.points_breakdown.man_of_match.correct
                                                  ? 'text-green-800'
                                                  : 'text-red-800'
                                                : 'text-purple-600'
                                            }`}>{prediction.mom_prediction}</span>
                                            {isCompleted && prediction.points_breakdown?.man_of_match && (
                                              prediction.points_breakdown.man_of_match.correct ? (
                                                <span className="text-green-600 text-xs sm:text-sm">✓</span>
                                              ) : (
                                                <span className="text-red-600 text-xs sm:text-sm">✗</span>
                                              )
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-gray-400 font-medium text-xs sm:text-sm">-</span>
                                        )}
                                      </td>
                                      {showPointsColumn && (() => {
                                        const pts = prediction?.points_awarded ?? 0;
                                        return (
                                          <td className="text-center py-2 sm:py-3 px-2 sm:px-4">
                                            {pts !== 0 ? (
                                              <span className="font-bold text-xs sm:text-sm md:text-base text-primary bg-accent/10 px-2 sm:px-3 py-0.5 sm:py-1 rounded border border-accent/20">
                                                {pts}
                                              </span>
                                            ) : (
                                              <span className="text-gray-400 font-medium text-xs sm:text-sm">0</span>
                                            )}
                                          </td>
                                        );
                                      })()}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            </div>
                          </div>

                          </div>
                        ) : (
                          <div className="text-center text-gray-600 bg-gray-50 rounded-lg p-6 border border-gray-100">
                            <p className="text-lg">Picks will be visible once the match starts</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
            </div>
          )}

          {activeTab === 'standings' && (
            <div className="space-y-6 min-w-0 max-w-full">
              <div className="flex justify-between items-center min-w-0">
                <h2 className="text-2xl font-bold text-white flex items-center min-w-0 truncate">
                  <span className="mr-3 shrink-0">📊</span>
                  <span className="truncate">League Standings</span>
                </h2>
              </div>
              <p className="text-sm text-white/80 -mt-2 break-words">
                {standingsSubview === 'matrix'
                  ? 'Points by phase for everyone in this league. Click a column header to sort.'
                  : `Click a player to see their picks and points for each match in this league only${
                      standingsScope === 'phase' && standingsPhaseId
                        ? ` (current view: ${
                            pickemPhasesConfig?.phases?.find((p) => p.id === standingsPhaseId)?.label || 'phase'
                          }).`
                        : '.'
                    }`}
              </p>

              {pickemPhasesConfig?.phases?.length ? (
                <div className="inline-flex rounded-lg border border-white/20 bg-white/10 p-0.5">
                  <button
                    type="button"
                    onClick={() => setStandingsSubview('leaderboard')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      standingsSubview === 'leaderboard'
                        ? 'bg-white text-primary shadow'
                        : 'text-white/90 hover:bg-white/10'
                    }`}
                  >
                    Leaderboard
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStandingsSubview('matrix');
                      loadPhaseMatrix();
                    }}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      standingsSubview === 'matrix'
                        ? 'bg-white text-primary shadow'
                        : 'text-white/90 hover:bg-white/10'
                    }`}
                  >
                    Phase matrix
                  </button>
                </div>
              ) : null}

              {standingsSubview === 'matrix' && pickemPhasesConfig?.phases?.length ? (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-w-0 max-w-full">
                  {phaseMatrixLoading && (
                    <p className="text-gray-600 text-center py-8 text-sm">Loading phase matrix…</p>
                  )}
                  {phaseMatrixError && !phaseMatrixLoading && (
                    <p className="text-red-600 text-center py-8 text-sm">{phaseMatrixError}</p>
                  )}
                  {!phaseMatrixLoading && phaseMatrix && phaseMatrix.phases.length === 0 && (
                    <p className="text-gray-600 text-center py-8 text-sm">No phase configuration for this tournament.</p>
                  )}
                  {!phaseMatrixLoading && phaseMatrix && phaseMatrix.phases.length > 0 && (
                    <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain">
                      <table className="w-full min-w-[520px] text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left py-3 px-3 text-primary font-bold sticky left-0 bg-gray-50 z-10 min-w-[8rem]">
                              Player
                            </th>
                            <th className="text-center py-3 px-2 text-primary font-bold whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => toggleMatrixSort('cumulative')}
                                className="font-bold text-primary hover:underline"
                              >
                                All Points
                                {matrixSort.key === 'cumulative'
                                  ? matrixSort.dir === 'desc'
                                    ? ' \u2193'
                                    : ' \u2191'
                                  : ''}
                              </button>
                            </th>
                            {phaseMatrix.phases.map((ph) => (
                              <th key={ph.id} className="text-center py-3 px-2 text-primary font-bold whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() => toggleMatrixSort(ph.id)}
                                  className="font-bold text-primary hover:underline max-w-[7rem] truncate inline-block align-bottom"
                                  title={ph.label}
                                >
                                  {ph.label}
                                  {matrixSort.key === ph.id
                                    ? matrixSort.dir === 'desc'
                                      ? ' \u2193'
                                      : ' \u2191'
                                    : ''}
                                </button>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedMatrixRows.map((r) => (
                            <tr key={r.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-2 px-3 sticky left-0 bg-white z-10 font-medium text-primary truncate max-w-[10rem]">
                                {r.display_name}
                                {r.is_manager ? (
                                  <span className="ml-1 text-[10px] uppercase text-gray-500">Mgr</span>
                                ) : null}
                              </td>
                              <td className="text-center py-2 px-2 font-semibold tabular-nums">
                                {r.cumulative_points}
                              </td>
                              {phaseMatrix.phases.map((ph) => (
                                <td key={ph.id} className="text-center py-2 px-2 tabular-nums text-gray-800">
                                  {r.phase_points[ph.id] ?? 0}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : null}

              {standingsSubview === 'leaderboard' ? (
                <>
                  {pickemPhasesConfig?.phases?.length ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="inline-flex rounded-lg border border-white/20 bg-white/10 p-0.5">
                        <button
                          type="button"
                          onClick={async () => {
                            if (!id) return;
                            const leagueId = parseInt(id, 10);
                            if (Number.isNaN(leagueId)) return;
                            setStandingsScope('cumulative');
                            setStandingsPhaseId(null);
                            const d = await apiService.getPickemStandings(leagueId);
                            setStandings((d as any).standings || []);
                          }}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            standingsScope === 'cumulative'
                              ? 'bg-white text-primary shadow'
                              : 'text-white/90 hover:bg-white/10'
                          }`}
                        >
                          Cumulative
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!id || !pickemPhasesConfig?.phases?.length) return;
                            const leagueId = parseInt(id, 10);
                            if (Number.isNaN(leagueId)) return;
                            const nextId =
                              standingsPhaseId ||
                              standingsCurrentPhaseId ||
                              pickemPhasesConfig.phases[0].id;
                            setStandingsScope('phase');
                            setStandingsPhaseId(nextId);
                            const d = await apiService.getPickemStandings(leagueId, {
                              scope: 'phase',
                              phase_id: nextId
                            });
                            setStandings((d as any).standings || []);
                          }}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            standingsScope === 'phase'
                              ? 'bg-white text-primary shadow'
                              : 'text-white/90 hover:bg-white/10'
                          }`}
                        >
                          Phase
                        </button>
                      </div>
                      {standingsScope === 'phase' && (
                        <label className="text-sm text-white/90 flex items-center gap-2">
                          <span className="sr-only">Phase</span>
                          <select
                            value={standingsPhaseId || ''}
                            onChange={async (e) => {
                              const v = e.target.value;
                              if (!id || !v) return;
                              const leagueId = parseInt(id, 10);
                              if (Number.isNaN(leagueId)) return;
                              setStandingsPhaseId(v);
                              const d = await apiService.getPickemStandings(leagueId, {
                                scope: 'phase',
                                phase_id: v
                              });
                              setStandings((d as any).standings || []);
                            }}
                            className="rounded-lg border border-white/30 bg-white/10 text-white text-sm px-3 py-2 min-w-[10rem]"
                          >
                            {pickemPhasesConfig.phases.map((p) => (
                              <option key={p.id} value={p.id} className="text-gray-900">
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                    </div>
                  ) : null}

                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-w-0 max-w-full">
                    <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain">
                      <table className="w-full min-w-0">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left py-4 px-6 text-primary font-bold">Rank</th>
                            <th className="text-left py-4 px-6 text-primary font-bold">Player</th>
                            <th className="text-center py-4 px-6 text-primary font-bold">Record</th>
                            <th className="text-center py-4 px-6 text-primary font-bold">Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {standings.map((standing, index) => {
                            const w = standing.wins ?? standing.correct_predictions ?? 0;
                            const l = standing.losses ?? standing.wrong_predictions ?? 0;
                            return (
                              <tr
                                key={standing.user_id}
                                className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
                                onClick={() => openStandingBreakdown(standing.user_id, standing.total_points)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openStandingBreakdown(standing.user_id, standing.total_points);
                                  }
                                }}
                                tabIndex={0}
                                role="button"
                                aria-label={`View picks and points for ${standing.display_name}`}
                              >
                                <td className="py-4 px-6">
                                  <span
                                    className={`font-bold text-lg ${
                                      index === 0
                                        ? 'text-accent'
                                        : index === 1
                                          ? 'text-gray-500'
                                          : index === 2
                                            ? 'text-amber-600'
                                            : 'text-gray-600'
                                    }`}
                                  >
                                    #{index + 1}
                                  </span>
                                </td>
                                <td className="py-4 px-4 sm:px-6 min-w-0">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="font-medium text-primary truncate">{standing.display_name}</span>
                                    {standing.is_manager && (
                                      <span className="shrink-0 px-2 py-1 bg-accent text-primary text-xs font-medium rounded-full">
                                        Manager
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="text-center py-4 px-6 text-gray-800 font-semibold tabular-nums">
                                  {`${w}\u2013${l}`}
                                </td>
                                <td className="text-center py-4 px-6">
                                  <span className="font-bold text-lg text-primary bg-accent/10 px-3 py-1 rounded border border-accent/20">
                                    {standing.total_points}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {pickemPhasesConfig?.phases?.length ? (
                    <details className="rounded-xl border border-white/20 bg-white/5 text-white/95 text-sm max-w-full">
                      <summary className="cursor-pointer px-4 py-3 font-medium list-none [&::-webkit-details-marker]:hidden">
                        Schedule by phase ({phaseScheduleSections.length} phases) — reference
                      </summary>
                      <div className="px-4 pb-4 space-y-4 border-t border-white/10 pt-3">
                        {phaseScheduleSections.map((sec) => (
                          <div key={sec.id}>
                            <p className="font-semibold text-white mb-1">{sec.label}</p>
                            {sec.resolved.length === 0 ? (
                              <p className="text-white/60 text-xs">No matches on schedule matched these IDs yet.</p>
                            ) : (
                              <ul className="space-y-1 text-white/85 text-xs sm:text-sm">
                                {sec.resolved.map((m) => (
                                  <li key={m.id} className="truncate">
                                    <span className="font-mono text-white/50 mr-1">{m.match_number ?? '·'}</span>
                                    {m.team1_shortname && m.team2_shortname
                                      ? `${m.team1_shortname} vs ${m.team2_shortname}`
                                      : m.name?.replace(/^Match \d+ - /, '') || m.id}
                                    {m.match_date ? (
                                      <span className="text-white/50 ml-2">{formatBreakdownDateShort(m.match_date)}</span>
                                    ) : null}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))}
                        {unassignedMatchCount > 0 ? (
                          <p className="text-white/60 text-xs pt-2 border-t border-white/10">
                            {unassignedMatchCount} match{unassignedMatchCount !== 1 ? 'es' : ''} not in any phase
                            (cumulative only).
                          </p>
                        ) : null}
                      </div>
                    </details>
                  ) : null}
                </>
              ) : null}
            </div>
          )}

    </div>
    </div>
    </div>

    {/* League Settings Modal */}
    {showSettingsModal && league && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-black/60"
        onClick={() => setShowSettingsModal(false)}
      >
        <div
          className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">League Settings</p>
              <h3 className="text-lg sm:text-xl font-semibold text-primary">{league.name}</h3>
            </div>
            <div className="flex items-center space-x-2">
              {leagueHasStarted && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                  Locked - League started
                </span>
              )}
              <button
                onClick={() => setShowSettingsModal(false)}
                className="p-2 rounded-full hover:bg-gray-200 text-gray-600"
                aria-label="Close settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
            <LeagueSettings
              leagueId={league.id}
              isManager={isManager}
              leagueHasStarted={leagueHasStarted}
              dynamicScoringEnabled={league.dynamic_scoring_enabled ?? false}
              matchStartPickSummaryEmail={league.pickem_email_match_start_pick_summary ?? false}
              pickemAllPicksAlwaysVisible={league.pickem_all_picks_always_visible ?? false}
              onMatchStartPickSummaryChange={() => loadLeagueData()}
              onPickemAllPicksVisibilityChange={() => loadLeagueData()}
              onDynamicScoringChange={() => loadLeagueData()}
              onSettingsUpdated={() => {
                loadLeagueData();
                setShowSettingsModal(false);
              }}
              onLeaveLeague={handleLeaveLeague}
              onLeagueDeleted={handleLeagueDeleted}
            />
          </div>

          <div className="px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
            <button
              onClick={() => setShowSettingsModal(false)}
              className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
    {(standingBreakdown || standingBreakdownLoading || standingBreakdownError) && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-black/60"
        onClick={closeStandingBreakdown}
      >
        <div
          className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Standings breakdown</p>
              <h3 className="text-lg font-semibold text-primary">
                {standingBreakdown?.display_name ?? 'Player'}
                <span className="text-gray-500 font-normal text-sm ml-2">
                  ({standingBreakdown?.matches?.length ?? '…'} matches
                  {standingBreakdown?.phase_id
                    ? ` · ${standingBreakdown.phase_label ||
                        pickemPhasesConfig?.phases?.find((p) => p.id === standingBreakdown.phase_id)?.label ||
                        standingBreakdown.phase_id}`
                    : ''}
                  )
                </span>
              </h3>
            </div>
            <button
              type="button"
              onClick={closeStandingBreakdown}
              className="p-2 rounded-full hover:bg-gray-200 text-gray-600"
              aria-label="Close breakdown"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
            {standingBreakdownLoading && (
              <p className="text-gray-600 text-center py-8">Loading…</p>
            )}
            {standingBreakdownError && !standingBreakdownLoading && (
              <p className="text-red-600 text-center py-8">{standingBreakdownError}</p>
            )}
            {standingBreakdown && !standingBreakdownLoading && (
              <>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left text-primary">
                        <th className="py-2 px-3 font-semibold">Match</th>
                        <th className="py-2 px-3 font-semibold">Pick</th>
                        <th className="py-2 px-3 font-semibold">Result</th>
                        <th className="py-2 px-3 font-semibold text-right">Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standingBreakdown.matches.map((m) => {
                        const sched = matchById.get(String(m.match_id));
                        const ord = extractOrdinalMatchLabel(m.match_name);
                        const t1s = sched?.team1_shortname;
                        const t2s = sched?.team2_shortname;
                        const t1f = sched?.team1_name;
                        const t2f = sched?.team2_name;
                        const primary =
                          ord && t1s && t2s
                            ? `${ord} · ${t1s} vs ${t2s}`
                            : t1s && t2s
                              ? `${t1s} vs ${t2s}`
                              : m.match_name && m.match_name.length > 52
                                ? `${m.match_name.slice(0, 49)}…`
                                : m.match_name || `Match ${m.match_id}`;
                        const norm = (s: string) => s.trim().toLowerCase();
                        const shortTeam = (full: string | null | undefined) => {
                          if (!full?.trim()) return '—';
                          const f = full.trim();
                          if (sched) {
                            if (t1f && norm(f) === norm(t1f)) return t1s || f;
                            if (t2f && norm(f) === norm(t2f)) return t2s || f;
                          }
                          return f.length > 14 ? `${f.slice(0, 12)}…` : f;
                        };
                        const dateShort = m.match_date ? formatBreakdownDateShort(m.match_date) : '';
                        return (
                        <tr key={m.match_id} className="border-t border-gray-100">
                          <td className="py-2 px-3 align-top max-w-[11rem] sm:max-w-none">
                            <div className="font-medium text-gray-900 leading-snug break-words" title={m.match_name || undefined}>
                              {primary}
                            </div>
                            {dateShort ? (
                              <div className="text-xs text-gray-500" title={m.match_date ? String(m.match_date) : undefined}>
                                {dateShort}
                              </div>
                            ) : null}
                          </td>
                          <td className="py-2 px-3 align-top text-gray-800 max-w-[6rem] sm:max-w-[8rem] break-words">
                            {m.predicted_winner?.trim() ? shortTeam(m.predicted_winner) : '—'}
                          </td>
                          <td className="py-2 px-3 align-top text-gray-700 max-w-[6rem] sm:max-w-[8rem] break-words">
                            {m.actual_winner?.trim() ? shortTeam(m.actual_winner) : '—'}
                          </td>
                          <td className="py-2 px-3 align-top text-right font-semibold text-primary">
                            {m.points}
                            {m.points_detail &&
                              (m.points_detail.streak_bonus !== 0 ||
                                m.points_detail.total_runs !== 0 ||
                                m.points_detail.mom !== 0) && (
                                <div className="text-xs font-normal text-gray-500 mt-0.5">
                                  {m.points_detail.base !== 0 && <span>base {m.points_detail.base} </span>}
                                  {m.points_detail.streak_bonus !== 0 && (
                                    <span>streak +{m.points_detail.streak_bonus} </span>
                                  )}
                                  {m.points_detail.wrong_penalty !== 0 && (
                                    <span>pen {m.points_detail.wrong_penalty} </span>
                                  )}
                                  {m.points_detail.total_runs !== 0 && <span>runs +{m.points_detail.total_runs} </span>}
                                  {m.points_detail.mom !== 0 && <span>MoM +{m.points_detail.mom}</span>}
                                </div>
                              )}
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-4 text-sm">
                  <div>
                    <span className="text-gray-600">Total (this league): </span>
                    <span className="font-bold text-primary text-lg">{standingBreakdown.total_points}</span>
                  </div>
                  {standingBreakdownExpectedTotal !== null &&
                    standingBreakdownExpectedTotal !== standingBreakdown.total_points && (
                      <div className="text-amber-700 text-xs">
                        Standings table showed {standingBreakdownExpectedTotal}; refresh standings if these differ.
                      </div>
                    )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default PickemLeaguePage;